import { CustomerOrder, OrderStatus, PaymentStatus, ProductSizeOption } from '../types';
import { getSupabase, isSupabaseConfigured, getCustomerSessionToken, fetchContentFromSupabase } from './supabase';
import { processOrderPointsEarning, processReferralRewardIfEligible } from './supabaseLoyalty';
import { isMenuItemAvailableForOutlet } from './supabaseStock';
import { normalizeIndonesianPhone } from './phone';
import { matchesOutlet } from '../data/adminAccounts';
import { getApiUrl } from './api';
import { safeSetItem, safeGetItem, stripHeavyBase64Images } from './safeStorage';
import { normalizeOutletKey } from './supabaseOutletStatus';
import { calculateItemUnitPrice, DEFAULT_SIZES, DEFAULT_SIZE } from '../data/addOnsData';
import { FIXED_SERVICE_FEE, calculateAuthoritativeOrderTotal, resolvePersistedOrderServiceFee } from './deliveryFee';
import { initialLetonData } from '../data/initialData';

const ORDERS_STORAGE_KEY = 'leton_orders_history';
const ADMIN_ORDERS_CACHE_KEY = 'leton_admin_orders_cache';

export const ORDERS_SQL_SCHEMA = `-- ==============================================================================
-- LETON COFFEE DUMAI - DATABASE MIGRATION & RLS POLICIES
-- Outlets, Orders, Order Items, Realtime, & Storage 'leton-images'
-- ==============================================================================

-- 1. Buat Tabel Outlets (Cabang Leton Coffee)
CREATE TABLE IF NOT EXISTS public.outlets (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  short_name TEXT,
  address TEXT,
  hours TEXT,
  image TEXT,
  badge TEXT,
  whatsapp TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- Seed data outlets dasar jika belum ada
INSERT INTO public.outlets (id, name, short_name, address, hours, badge)
VALUES
  ('sudirman', 'Leton Coffee — Jalan Jendral Sudirman', 'Leton Sudirman', 'Jl. Jend. Sudirman No. 88, Dumai Kota, Riau', '08:00 – 23:00 WIB', 'CHAPTER 5 • URBAN HUB'),
  ('kelakap_7', 'Leton Coffee — Ratusima / Kelakap 7', 'Leton Kelakap 7', 'Jl. Ratu Sima / Kelakap 7, Dumai Barat, Riau', '09:00 – 23:30 WIB', 'CHAPTER 6 • OPEN AIR SPOT'),
  ('letgo-mpp', 'LetGo — depan MPP', 'LetGo MPP', 'Area Parkir Depan Mall Pelayanan Publik (MPP), Dumai', '16:00 – 22:30 WIB', 'MOBILE COFFEE BOOTH')
ON CONFLICT (id) DO UPDATE SET
  name = EXCLUDED.name,
  short_name = EXCLUDED.short_name,
  address = EXCLUDED.address,
  hours = EXCLUDED.hours,
  badge = EXCLUDED.badge;

-- 2. Buat Tabel Orders (Pesanan Pelanggan Online)
CREATE TABLE IF NOT EXISTS public.orders (
  id TEXT PRIMARY KEY,
  order_number TEXT NOT NULL UNIQUE,
  outlet_id TEXT NOT NULL,
  outlet_name TEXT NOT NULL,
  customer_name TEXT NOT NULL,
  customer_phone TEXT,
  order_type TEXT NOT NULL, -- 'DINE IN' | 'TAKE AWAY'
  table_number TEXT,
  items JSONB NOT NULL DEFAULT '[]'::jsonb,
  total_amount NUMERIC NOT NULL DEFAULT 0,
  payment_method TEXT NOT NULL, -- 'QRIS' | 'TUNAI'
  payment_status TEXT NOT NULL DEFAULT 'WAITING PAYMENT', -- 'WAITING PAYMENT' | 'WAITING VERIFICATION' | 'PAY AT STORE' | 'PAID' | 'PAYMENT REJECTED'
  payment_proof_path TEXT, -- Path file bukti transfer di Supabase Storage
  payment_receipt_url TEXT, -- URL file bukti pembayaran
  payment_receipt_path TEXT, -- Path unik file bukti pembayaran
  rejection_reason TEXT, -- Catatan penolakan jika pembayaran / pesanan ditolak
  order_status TEXT NOT NULL DEFAULT 'NEW', -- 'NEW' | 'ACCEPTED' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED'
  customer_note TEXT,
  payment_verified_at TIMESTAMPTZ, -- Waktu verifikasi/penolakan pembayaran untuk retensi 24 jam
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- Pastikan seluruh kolom wajib tersedia jika tabel sudah ada sebelumnya
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS outlet_id TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS outlet_name TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS customer_phone TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS table_number TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS payment_proof_path TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS payment_receipt_url TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS payment_receipt_path TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS rejection_reason TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS customer_note TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS pickup_time TEXT;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS payment_verified_at TIMESTAMPTZ;
ALTER TABLE public.orders ADD COLUMN IF NOT EXISTS updated_at TIMESTAMPTZ DEFAULT NOW();

-- 3. Buat Tabel Order Items (Relasi Produk)
CREATE TABLE IF NOT EXISTS public.order_items (
  id TEXT PRIMARY KEY,
  order_id TEXT REFERENCES public.orders(id) ON DELETE CASCADE,
  product_id TEXT NOT NULL,
  name TEXT NOT NULL,
  price NUMERIC NOT NULL,
  quantity INTEGER NOT NULL DEFAULT 1,
  image TEXT,
  note TEXT,
  topping JSONB,
  syrup JSONB,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- 4. Aktifkan Row Level Security (RLS)
ALTER TABLE public.orders ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.order_items ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.outlets ENABLE ROW LEVEL SECURITY;

-- Helper Function: Ekstrak Role Admin dari Request Header Supabase
CREATE OR REPLACE FUNCTION public.get_current_admin_role()
RETURNS TEXT AS $$
BEGIN
  RETURN COALESCE(
    current_setting('request.headers', true)::json->>'x-admin-role',
    ''
  );
EXCEPTION WHEN OTHERS THEN
  RETURN '';
END;
$$ LANGUAGE plpgsql STABLE;

-- Helper Function: Ekstrak Outlet ID Admin dari Request Header Supabase
CREATE OR REPLACE FUNCTION public.get_current_outlet_id()
RETURNS TEXT AS $$
BEGIN
  RETURN COALESCE(
    current_setting('request.headers', true)::json->>'x-outlet-id',
    ''
  );
EXCEPTION WHEN OTHERS THEN
  RETURN '';
END;
$$ LANGUAGE plpgsql STABLE;

-- 5. Kebijakan Keamanan RLS Orders (SUPER ADMIN & OUTLET ADMIN ISOLATION)
DROP POLICY IF EXISTS "Public Read Access Orders" ON public.orders;
DROP POLICY IF EXISTS "Public Insert Access Orders" ON public.orders;
DROP POLICY IF EXISTS "Public Update Access Orders" ON public.orders;
DROP POLICY IF EXISTS "Orders Super Admin Full Access" ON public.orders;
DROP POLICY IF EXISTS "Orders Super Admin Read Only" ON public.orders;
DROP POLICY IF EXISTS "Orders Outlet Admin Access" ON public.orders;
DROP POLICY IF EXISTS "Orders Public Insert" ON public.orders;
DROP POLICY IF EXISTS "Orders Public Read Open" ON public.orders;

-- a. SUPER ADMIN:
-- Super Admin memiliki hak penuh (SELECT, UPDATE, DELETE) untuk manajemen data pesanan
CREATE POLICY "Orders Super Admin Full Access" ON public.orders
FOR ALL TO anon, authenticated
USING (
  get_current_admin_role() = 'super_admin'
)
WITH CHECK (
  get_current_admin_role() = 'super_admin'
);

-- b. OUTLET ADMIN:
-- Hanya dapat membaca dan mengupdate pesanan yang terdaftar di outlet miliknya sendiri.
-- Mendukung isolasi ketat Sudirman dan Kelakap 7 (termasuk alias Ratusima).
CREATE POLICY "Orders Outlet Admin Access" ON public.orders
FOR ALL TO anon, authenticated
USING (
  get_current_admin_role() = 'outlet_admin'
  AND (
    outlet_id = get_current_outlet_id()
    OR (get_current_outlet_id() = 'kelakap_7' AND (outlet_id = 'kelakap_7' OR outlet_id = 'ratusima' OR outlet_id ILIKE '%kelakap%'))
    OR (get_current_outlet_id() = 'sudirman' AND (outlet_id = 'sudirman' OR outlet_id ILIKE '%sudirman%'))
    OR (get_current_outlet_id() = 'letgo-mpp' AND (outlet_id = 'letgo-mpp' OR outlet_id ILIKE '%letgo%'))
  )
)
WITH CHECK (
  get_current_admin_role() = 'outlet_admin'
  AND (
    outlet_id = get_current_outlet_id()
    OR (get_current_outlet_id() = 'kelakap_7' AND (outlet_id = 'kelakap_7' OR outlet_id = 'ratusima' OR outlet_id ILIKE '%kelakap%'))
    OR (get_current_outlet_id() = 'sudirman' AND (outlet_id = 'sudirman' OR outlet_id ILIKE '%sudirman%'))
    OR (get_current_outlet_id() = 'letgo-mpp' AND (outlet_id = 'letgo-mpp' OR outlet_id ILIKE '%letgo%'))
  )
);

-- c. Customer Publik: Dapat membuat pesanan baru
CREATE POLICY "Orders Public Insert" ON public.orders
FOR INSERT TO anon, authenticated
WITH CHECK (true);

-- d. Customer Publik: Dapat membaca pesanan untuk pelacakan status pesanan
CREATE POLICY "Orders Public Read Open" ON public.orders
FOR SELECT TO anon, authenticated
USING (
  get_current_admin_role() = '' OR get_current_admin_role() IS NULL
);

-- 6. Kebijakan Keamanan RLS Order Items
DROP POLICY IF EXISTS "Order Items Public Select" ON public.order_items;
DROP POLICY IF EXISTS "Order Items Public Insert" ON public.order_items;

CREATE POLICY "Order Items Public Select" ON public.order_items
FOR SELECT TO anon, authenticated
USING (true);

CREATE POLICY "Order Items Public Insert" ON public.order_items
FOR INSERT TO anon, authenticated
WITH CHECK (true);

-- 7. Kebijakan Keamanan RLS Outlets
DROP POLICY IF EXISTS "Outlets Public Select" ON public.outlets;
DROP POLICY IF EXISTS "Outlets Super Admin Manage" ON public.outlets;

CREATE POLICY "Outlets Public Select" ON public.outlets
FOR SELECT TO anon, authenticated
USING (true);

CREATE POLICY "Outlets Super Admin Manage" ON public.outlets
FOR ALL TO anon, authenticated
USING (get_current_admin_role() = 'super_admin')
WITH CHECK (get_current_admin_role() = 'super_admin');

-- 8. STORAGE BUCKET 'leton-images' (Foto Menu & Bukti Transfer QRIS)
-- Membuat bucket publik 'leton-images' dengan batas ukuran 10MB
INSERT INTO storage.buckets (id, name, public, file_size_limit, allowed_mime_types)
VALUES (
  'leton-images',
  'leton-images',
  true,
  10485760, -- 10MB
  ARRAY['image/jpeg', 'image/png', 'image/webp', 'image/jpg']
)
ON CONFLICT (id) DO UPDATE SET
  public = true,
  file_size_limit = 10485760;

-- Kebijakan Storage leton-images: Public Upload
DROP POLICY IF EXISTS "Public Upload to leton-images" ON storage.objects;
CREATE POLICY "Public Upload to leton-images"
ON storage.objects FOR INSERT TO public
WITH CHECK (bucket_id = 'leton-images');

-- Kebijakan Storage leton-images: Public View
DROP POLICY IF EXISTS "Public View leton-images" ON storage.objects;
CREATE POLICY "Public View leton-images"
ON storage.objects FOR SELECT TO public
USING (bucket_id = 'leton-images');

-- Kebijakan Storage leton-images: Public Update
DROP POLICY IF EXISTS "Public Update leton-images" ON storage.objects;
CREATE POLICY "Public Update leton-images"
ON storage.objects FOR UPDATE TO public
USING (bucket_id = 'leton-images');

-- Kebijakan Storage leton-images: Public Delete
DROP POLICY IF EXISTS "Public Delete leton-images" ON storage.objects;
CREATE POLICY "Public Delete leton-images"
ON storage.objects FOR DELETE TO public
USING (bucket_id = 'leton-images');

-- 9. Aktifkan Realtime Replication untuk Tabel Orders
ALTER PUBLICATION supabase_realtime ADD TABLE public.orders;

-- ==============================================================================
-- LETON COFFEE DUMAI - LOYALTY SYSTEM SCHEMA
-- ==============================================================================

-- 10. Table: Loyalty Settings
CREATE TABLE IF NOT EXISTS public.loyalty_settings (
  id TEXT PRIMARY KEY DEFAULT 'default',
  is_active BOOLEAN NOT NULL DEFAULT true,
  earning_amount_per_point NUMERIC NOT NULL DEFAULT 10000,
  calculation_basis TEXT NOT NULL DEFAULT 'SUBTOTAL', -- 'SUBTOTAL' | 'TOTAL' (after discount)
  expiration_mode TEXT NOT NULL DEFAULT 'NEVER', -- 'NEVER' | 'DAYS'
  expiration_days INTEGER NOT NULL DEFAULT 365,
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- Seed default settings
INSERT INTO public.loyalty_settings (id, is_active, earning_amount_per_point, calculation_basis, expiration_mode, expiration_days)
VALUES ('default', true, 10000, 'SUBTOTAL', 'NEVER', 365)
ON CONFLICT (id) DO NOTHING;

-- 11. Table: Loyalty Rewards
CREATE TABLE IF NOT EXISTS public.loyalty_rewards (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  description TEXT,
  points_required INTEGER NOT NULL,
  reward_type TEXT NOT NULL, -- 'DISCOUNT_PERCENT' | 'DISCOUNT_NOMINAL' | 'FREE_ITEM'
  reward_value NUMERIC NOT NULL,
  is_active BOOLEAN NOT NULL DEFAULT true,
  redeem_limit INTEGER,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- Seed default rewards
INSERT INTO public.loyalty_rewards (id, name, description, points_required, reward_type, reward_value, is_active)
VALUES 
  ('rwd-1', 'Potongan Rp5.000', 'Diskon langsung Rp5.000 untuk transaksi berikutnya.', 15, 'DISCOUNT_NOMINAL', 5000, true),
  ('rwd-2', 'Potongan Rp10.000', 'Diskon langsung Rp10.000 untuk transaksi berikutnya.', 28, 'DISCOUNT_NOMINAL', 10000, true),
  ('rwd-3', 'Free Redvelvet Leton', 'Klaim 1x Cup Redvelvet Leton gratis.', 40, 'FREE_ITEM', 22000, true)
ON CONFLICT (id) DO NOTHING;

-- 12. Ensure columns in public.customers for loyalty balances
ALTER TABLE public.customers ADD COLUMN IF NOT EXISTS points_balance INTEGER NOT NULL DEFAULT 0;
ALTER TABLE public.customers ADD COLUMN IF NOT EXISTS total_points_earned INTEGER NOT NULL DEFAULT 0;
ALTER TABLE public.customers ADD COLUMN IF NOT EXISTS total_points_redeemed INTEGER NOT NULL DEFAULT 0;

-- 13. Table: Loyalty Point Transactions
CREATE TABLE IF NOT EXISTS public.loyalty_transactions (
  id TEXT PRIMARY KEY,
  customer_id TEXT REFERENCES public.customers(id) ON DELETE CASCADE,
  transaction_type TEXT NOT NULL, -- 'EARN' | 'REDEEM' | 'MANUAL_ADD' | 'MANUAL_SUB' | 'EXPIRED'
  points INTEGER NOT NULL, -- positive for credit, negative for debit
  balance_before INTEGER NOT NULL,
  balance_after INTEGER NOT NULL,
  reference_order_id TEXT REFERENCES public.orders(id) ON DELETE SET NULL,
  reference_reward_id TEXT REFERENCES public.loyalty_rewards(id) ON DELETE SET NULL,
  reason TEXT,
  admin_username TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- 14. Table: Reward Redemptions (Vouchers)
CREATE TABLE IF NOT EXISTS public.reward_redemptions (
  id TEXT PRIMARY KEY,
  customer_id TEXT REFERENCES public.customers(id) ON DELETE CASCADE,
  reward_id TEXT REFERENCES public.loyalty_rewards(id) ON DELETE CASCADE,
  reward_name TEXT NOT NULL,
  reward_type TEXT NOT NULL,
  reward_value NUMERIC NOT NULL,
  points_spent INTEGER NOT NULL,
  status TEXT NOT NULL DEFAULT 'ACTIVE', -- 'ACTIVE' | 'USED' | 'EXPIRED'
  reference_order_id TEXT REFERENCES public.orders(id) ON DELETE SET NULL, -- order where it was used
  created_at TIMESTAMPTZ DEFAULT NOW(),
  expires_at TIMESTAMPTZ,
  used_at TIMESTAMPTZ
);

-- 15. Create secure function for manual adjustments and automatic point earnings
-- Ensures atomic updates to customer points balance and records transaction securely.
-- All operations are executed within a database transaction.
CREATE OR REPLACE FUNCTION public.adjust_customer_points(
  p_customer_id TEXT,
  p_points INTEGER, -- can be positive (earn/add) or negative (redeem/sub)
  p_type TEXT,
  p_reason TEXT,
  p_ref_order_id TEXT DEFAULT NULL,
  p_ref_reward_id TEXT DEFAULT NULL,
  p_admin TEXT DEFAULT NULL
)
RETURNS JSONB AS $$
DECLARE
  v_current_bal INTEGER := 0;
  v_new_bal INTEGER := 0;
  v_tx_id TEXT;
  v_earned_inc INTEGER := 0;
  v_redeemed_inc INTEGER := 0;
  v_customer_exists BOOLEAN;
BEGIN
  -- 1. Check if customer exists
  SELECT EXISTS(SELECT 1 FROM public.customers WHERE id = p_customer_id) INTO v_customer_exists;
  IF NOT v_customer_exists THEN
    RETURN jsonb_build_object('success', false, 'error', 'Customer tidak ditemukan');
  END IF;

  -- 2. Lock customer row and get current balance
  SELECT points_balance INTO v_current_bal
  FROM public.customers
  WHERE id = p_customer_id
  FOR UPDATE;

  -- Calculate new balance
  v_new_bal := v_current_bal + p_points;

  -- Prevent negative balance
  IF v_new_bal < 0 THEN
    RETURN jsonb_build_object('success', false, 'error', 'Saldo point tidak mencukupi');
  END IF;

  -- Set increments
  IF p_points > 0 THEN
    v_earned_inc := p_points;
  ELSE
    v_redeemed_inc := ABS(p_points);
  END IF;

  -- 3. Update customer table
  UPDATE public.customers
  SET 
    points_balance = v_new_bal,
    total_points_earned = total_points_earned + v_earned_inc,
    total_points_redeemed = total_points_redeemed + v_redeemed_inc,
    updated_at = NOW()
  WHERE id = p_customer_id;

  -- Generate transaction ID
  v_tx_id := 'TX-' || floor(random() * 900000 + 100000)::text;

  -- 4. Record transaction history
  INSERT INTO public.loyalty_transactions (
    id, customer_id, transaction_type, points, balance_before, balance_after,
    reference_order_id, reference_reward_id, reason, admin_username, created_at
  ) VALUES (
    v_tx_id, p_customer_id, p_type, p_points, v_current_bal, v_new_bal,
    p_ref_order_id, p_ref_reward_id, p_reason, p_admin, NOW()
  );

  RETURN jsonb_build_object(
    'success', true, 
    'transaction_id', v_tx_id, 
    'points_balance', v_new_bal,
    'points_adjusted', p_points
  );
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;


-- 16. Create secure function for reward redemption (atomic check-then-redeem)
CREATE OR REPLACE FUNCTION public.redeem_loyalty_reward(
  p_customer_id TEXT,
  p_reward_id TEXT
)
RETURNS JSONB AS $$
DECLARE
  v_points_required INTEGER;
  v_reward_name TEXT;
  v_reward_type TEXT;
  v_reward_value NUMERIC;
  v_is_active BOOLEAN;
  v_current_bal INTEGER;
  v_new_bal INTEGER;
  v_redemption_id TEXT;
  v_tx_res JSONB;
BEGIN
  -- 1. Get reward detail
  SELECT name, points_required, reward_type, reward_value, is_active
  INTO v_reward_name, v_points_required, v_reward_type, v_reward_value, v_is_active
  FROM public.loyalty_rewards
  WHERE id = p_reward_id;

  IF v_reward_name IS NULL THEN
    RETURN jsonb_build_object('success', false, 'error', 'Reward tidak ditemukan');
  END IF;

  IF NOT v_is_active THEN
    RETURN jsonb_build_object('success', false, 'error', 'Reward sedang tidak aktif');
  END IF;

  -- 2. Call adjust_customer_points to securely deduct points
  v_tx_res := public.adjust_customer_points(
    p_customer_id,
    -v_points_required,
    'REDEEM',
    'Redeem Reward: ' || v_reward_name,
    NULL,
    p_reward_id,
    NULL
  );

  IF NOT (v_tx_res->>'success')::boolean THEN
    RETURN jsonb_build_object('success', false, 'error', COALESCE(v_tx_res->>'error', 'Redeem gagal'));
  END IF;

  -- 3. Create redemption voucher record
  v_redemption_id := 'VCH-' || floor(random() * 900000 + 100000)::text;
  
  INSERT INTO public.reward_redemptions (
    id, customer_id, reward_id, reward_name, reward_type, reward_value, 
    points_spent, status, created_at, expires_at
  ) VALUES (
    v_redemption_id, p_customer_id, p_reward_id, v_reward_name, v_reward_type, v_reward_value,
    v_points_required, 'ACTIVE', NOW(), NOW() + INTERVAL '30 days' -- standard 30 days validation
  );

  RETURN jsonb_build_object(
    'success', true,
    'redemption_id', v_redemption_id,
    'reward_name', v_reward_name,
    'points_spent', v_points_required,
    'points_balance', (v_tx_res->>'points_balance')::integer
  );
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;


-- 17. Create secure function to trigger automatic point earning for a PAID/COMPLETED order.
-- Ensures that an order only awards points once.
CREATE OR REPLACE FUNCTION public.process_order_points_earning(
  p_order_id TEXT
)
RETURNS JSONB AS $$
DECLARE
  v_order_exists BOOLEAN;
  v_customer_id TEXT;
  v_total_amount NUMERIC;
  v_payment_status TEXT;
  v_order_status TEXT;
  v_is_active BOOLEAN;
  v_earning_amount NUMERIC;
  v_calc_basis TEXT;
  v_points_to_earn INTEGER;
  v_points_calculated NUMERIC;
  v_already_earned BOOLEAN;
  v_res JSONB;
BEGIN
  -- 1. Check if points earning already processed for this order
  SELECT EXISTS(
    SELECT 1 FROM public.loyalty_transactions 
    WHERE reference_order_id = p_order_id AND transaction_type = 'EARN'
  ) INTO v_already_earned;

  IF v_already_earned THEN
    RETURN jsonb_build_object('success', false, 'error', 'Poin untuk pesanan ini sudah pernah diproses');
  END IF;

  -- 2. Fetch order details
  SELECT EXISTS(SELECT 1 FROM public.orders WHERE id = p_order_id) INTO v_order_exists;
  IF NOT v_order_exists THEN
    RETURN jsonb_build_object('success', false, 'error', 'Order tidak ditemukan');
  END IF;

  SELECT customer_id, total_amount, payment_status, order_status
  INTO v_customer_id, v_total_amount, v_payment_status, v_order_status
  FROM public.orders
  WHERE id = p_order_id;

  -- Must have a valid customer_id linked
  IF v_customer_id IS NULL OR v_customer_id = '' THEN
    RETURN jsonb_build_object('success', false, 'error', 'Pesanan tidak ditautkan ke member customer_id');
  END IF;

  -- Order must be PAID or COMPLETED
  -- REMOVED: IF v_payment_status <> 'PAID' AND v_order_status <> 'COMPLETED' THEN ...

  -- 3. Fetch loyalty settings
  SELECT is_active, earning_amount_per_point, calculation_basis
  INTO v_is_active, v_earning_amount, v_calc_basis
  FROM public.loyalty_settings
  WHERE id = 'default';

  IF v_is_active IS NULL OR NOT v_is_active THEN
    RETURN jsonb_build_object('success', false, 'error', 'Sistem Loyalty Point sedang dinonaktifkan oleh Admin');
  END IF;

  -- 4. Calculate points
  -- Since orders subtotal is not a standalone DB numeric column, we use order total_amount
  v_points_calculated := floor(v_total_amount / v_earning_amount);
  v_points_to_earn := v_points_calculated::integer;

  IF v_points_to_earn <= 0 THEN
    RETURN jsonb_build_object('success', true, 'points_earned', 0, 'message', 'Nominal transaksi tidak mencapai batas minimum perolehan poin');
  END IF;

  -- 5. Credit points via adjust_customer_points
  v_res := public.adjust_customer_points(
    v_customer_id,
    v_points_to_earn,
    'EARN',
    'Earn point dari Pesanan #' || p_order_id,
    p_order_id,
    NULL,
    NULL
  );

  -- 6. Trigger referral reward if eligible (idempotent)
  PERFORM public.process_referral_reward(p_order_id, v_customer_id::uuid);

  RETURN v_res;
END;
$$ LANGUAGE plpgsql SECURITY DEFINER;


-- 18. Enable RLS and create public policies
ALTER TABLE public.loyalty_settings ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.loyalty_rewards ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.loyalty_transactions ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.reward_redemptions ENABLE ROW LEVEL SECURITY;

-- Allow reading loyalty settings and rewards for anyone
CREATE POLICY "Allow read loyalty_settings" ON public.loyalty_settings FOR SELECT USING (true);
CREATE POLICY "Allow read loyalty_rewards" ON public.loyalty_rewards FOR SELECT USING (true);

-- Allow admins full access to settings, rewards, and transactions
CREATE POLICY "Admins manage loyalty_settings" ON public.loyalty_settings 
  FOR ALL USING (get_current_admin_role() = 'super_admin');

CREATE POLICY "Admins manage loyalty_rewards" ON public.loyalty_rewards 
  FOR ALL USING (get_current_admin_role() = 'super_admin');

CREATE POLICY "Admins manage loyalty_transactions" ON public.loyalty_transactions 
  FOR ALL USING (get_current_admin_role() = 'super_admin');

CREATE POLICY "Admins manage reward_redemptions" ON public.reward_redemptions 
  FOR ALL USING (get_current_admin_role() = 'super_admin');

-- ==============================================================================
-- 19. Table: OneSignal Admin Subscriptions (Web Push Notification Subscriptions)
-- ==============================================================================
CREATE TABLE IF NOT EXISTS public.admin_onesignal_subscriptions (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  subscription_id TEXT NOT NULL UNIQUE,
  username TEXT NOT NULL DEFAULT 'admin',
  outlet_id TEXT NOT NULL DEFAULT 'sudirman',
  role TEXT NOT NULL DEFAULT 'outlet_admin',
  device_info TEXT,
  is_active BOOLEAN NOT NULL DEFAULT true,
  last_seen_at TIMESTAMPTZ DEFAULT NOW(),
  last_push_at TIMESTAMPTZ,
  last_push_status TEXT,
  last_error TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

-- Ensure all columns exist for existing deployments
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS is_active BOOLEAN NOT NULL DEFAULT true;
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS last_seen_at TIMESTAMPTZ DEFAULT NOW();
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS last_push_at TIMESTAMPTZ;
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS last_push_status TEXT;
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS last_error TEXT;
ALTER TABLE public.admin_onesignal_subscriptions ADD COLUMN IF NOT EXISTS updated_at TIMESTAMPTZ DEFAULT NOW();

-- Performance Indexes
CREATE INDEX IF NOT EXISTS idx_admin_onesignal_active_outlet 
  ON public.admin_onesignal_subscriptions(outlet_id, is_active);

CREATE INDEX IF NOT EXISTS idx_admin_onesignal_sub_id 
  ON public.admin_onesignal_subscriptions(subscription_id);

ALTER TABLE public.admin_onesignal_subscriptions ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Allow public read admin_onesignal_subscriptions" ON public.admin_onesignal_subscriptions FOR SELECT USING (true);
CREATE POLICY "Allow service and admin write admin_onesignal_subscriptions" ON public.admin_onesignal_subscriptions FOR ALL USING (true);
`;

/**
 * Generate unique order code e.g. "LTN-7842"
 */
export function generateOrderNumber(): string {
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `LTN-${randomNum}`;
}

/**
 * Save order to localStorage history (for customer review on their device)
 */
export function saveOrderToLocalHistory(order: CustomerOrder): void {
  try {
    const existing = getLocalOrderHistory();
    const filtered = existing.filter((o) => o.id !== order.id);
    // Keep heavy receipt images out of localStorage history to save quota
    const sanitizedOrder = stripHeavyBase64Images(order);
    const updated = [sanitizedOrder, ...filtered].slice(0, 20); // keep last 20 orders
    safeSetItem(ORDERS_STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Failed to save order to local history:', err);
  }
}

export function getLocalOrderHistory(): CustomerOrder[] {
  try {
    const raw = safeGetItem(ORDERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Helper to update order in local cache
 */
function updateLocalCache(order: CustomerOrder): void {
  try {
    const raw = safeGetItem(ADMIN_ORDERS_CACHE_KEY);
    let orders: CustomerOrder[] = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(orders)) orders = [];
    const sanitizedOrder = stripHeavyBase64Images(order);
    const index = orders.findIndex((o) => o.id === order.id);
    if (index >= 0) {
      orders[index] = sanitizedOrder;
    } else {
      orders.unshift(sanitizedOrder);
    }
    safeSetItem(ADMIN_ORDERS_CACHE_KEY, JSON.stringify(orders.slice(0, 50)));
  } catch (err) {
    console.warn('Local cache update failed:', err);
  }
}

class UploadTimeoutError extends Error {
  readonly isUploadTimeout = true;
  constructor(message: string) {
    super(message);
    this.name = 'UploadTimeoutError';
  }
}

function isUploadTimeoutError(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === 'object' &&
      ((err as any).isUploadTimeout === true || (err as any).name === 'AbortError')
  );
}

/**
 * Helper to race a promise against a strict timeout and optional AbortSignal
 */
function withUploadTimeout<T>(
  promise: PromiseLike<T>,
  ms: number,
  timeoutMessage: string,
  signal?: AbortSignal
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (signal?.aborted || ms <= 0) {
      reject(new UploadTimeoutError(timeoutMessage));
      return;
    }

    let settled = false;
    const cleanup = () => {
      clearTimeout(timer);
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    const onAbort = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new UploadTimeoutError(timeoutMessage));
    };

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new UploadTimeoutError(timeoutMessage));
    }, ms);

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }

    Promise.resolve(promise)
      .then((val) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(val);
      })
      .catch((err) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(err);
      });
  });
}

/**
 * Helper to compress image in browser to lightweight JPEG before saving.
 * Aborts FileReader if timeout or signal abort occurs while reading, and settles at most once.
 */
async function compressImageForCloud(
  file: File,
  maxDim = 1280,
  quality = 0.82,
  timeoutMs = 6000,
  signal?: AbortSignal
): Promise<string> {
  return new Promise((resolve) => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      signal?.aborted ||
      timeoutMs <= 0
    ) {
      resolve('');
      return;
    }

    let settled = false;
    let reader: FileReader | null = new FileReader();
    let img: HTMLImageElement | null = null;

    const cleanup = () => {
      clearTimeout(safetyTimer);
      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
      if (reader) {
        reader.onload = null;
        reader.onerror = null;
        reader.onabort = null;
      }
      if (img) {
        img.onload = null;
        img.onerror = null;
      }
    };

    const abortReaderIfActive = () => {
      if (reader && reader.readyState === FileReader.LOADING) {
        try {
          reader.abort();
        } catch {
          // ignore abort exceptions
        }
      }
    };

    const finish = (val: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(val);
    };

    const handleAbort = () => {
      abortReaderIfActive();
      finish('');
    };

    if (signal) {
      signal.addEventListener('abort', handleAbort, { once: true });
    }

    const effectiveTimeout = Math.max(500, Math.min(timeoutMs, 10000));
    const safetyTimer = setTimeout(() => {
      abortReaderIfActive();
      finish('');
    }, effectiveTimeout);

    try {
      reader.onload = (e) => {
        if (settled) return;
        const resultStr = (e.target?.result as string) || '';
        if (!resultStr) {
          finish('');
          return;
        }
        img = new Image();
        img.onload = () => {
          if (settled || !img) return;
          try {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
              if (width > height) {
                height = Math.round((height * maxDim) / width);
                width = maxDim;
              } else {
                width = Math.round((width * maxDim) / height);
                height = maxDim;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              finish(resultStr);
              return;
            }
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            finish(dataUrl);
          } catch {
            finish(resultStr);
          }
        };
        img.onerror = () => finish(resultStr);
        img.src = resultStr;
      };
      reader.onerror = () => finish('');
      reader.onabort = () => finish('');
      reader.readAsDataURL(file);
    } catch {
      abortReaderIfActive();
      finish('');
    }
  });
}

/**
 * Convert a base64 DataURL into a File object for binary storage upload
 */
function dataUrlToFile(dataUrl: string, fileName: string): File | null {
  try {
    const parts = dataUrl.split(',');
    if (parts.length < 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  } catch {
    return null;
  }
}

export interface ReceiptUploadOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  onBackgroundPendingChange?: (pending: boolean) => void;
}

export interface ReceiptUploadResult {
  success: boolean;
  url?: string;
  path?: string;
  error?: string;
  uncertain?: boolean;
}

export const TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS = 60000;

/**
 * Upload Payment Receipt (QRIS)
 * Valid formats: JPG, JPEG, PNG, WEBP (Max 5MB on Checkout UI, 10MB bucket limit)
 * Overall budget: Max 60,000 ms (60 seconds). Resolves immediately on success.
 * Multi-layer storage strategy with strict stage timeouts and ambiguity protection:
 * 1. Supabase Storage bucket 'leton-images' (Direct Cloud Storage)
 * 2. Backend API /api/upload-receipt (Node Server Storage) — only if Layer 1 definitively failed
 * 3. Supabase Cloud Database Record in 'leton_content' — only if Layer 1 & Layer 2 definitively failed
 */
export async function uploadPaymentReceipt(
  file: File,
  orderNumber: string,
  options?: ReceiptUploadOptions
): Promise<ReceiptUploadResult> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      success: false,
      error: 'Koneksi internet terputus. Periksa jaringan Anda lalu coba upload kembali.',
    };
  }

  // 1. Format validation (JPG, JPEG, PNG, WEBP)
  const validExtensions = ['jpg', 'jpeg', 'png', 'webp'];
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const validMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  if (!validExtensions.includes(ext) && !validMimes.includes(file.type)) {
    return {
      success: false,
      error: 'Format file tidak didukung. Harap upload file JPG, JPEG, PNG, atau WEBP.',
    };
  }

  // 2. Size validation (max 5MB as displayed in Checkout UI)
  if (file.size <= 0) {
    return {
      success: false,
      error: 'File bukti pembayaran kosong atau tidak valid.',
    };
  }

  if (file.size > 5 * 1024 * 1024) {
    return {
      success: false,
      error: 'Ukuran file maksimal 5 MB.',
    };
  }

  const totalBudgetMs = Math.min(
    Math.max(5000, options?.timeoutMs ?? TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS),
    TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS
  );
  const deadline = Date.now() + totalBudgetMs;
  const getRemainingMs = () => Math.max(0, deadline - Date.now());
  const signal = options?.signal;

  let activeSdkCount = 0;
  const trackSdkPromise = <T>(p: PromiseLike<T>): Promise<T> => {
    activeSdkCount += 1;
    options?.onBackgroundPendingChange?.(true);
    let released = false;
    const releaseOne = () => {
      if (released) return;
      released = true;
      clearTimeout(maxWaitTimer);
      activeSdkCount = Math.max(0, activeSdkCount - 1);
      if (activeSdkCount === 0) {
        options?.onBackgroundPendingChange?.(false);
      }
    };
    const maxWaitTimer = setTimeout(releaseOne, Math.max(1000, getRemainingMs()));
    return Promise.resolve(p).finally(releaseOne);
  };

  // 3. Optimize large images (> 350 KB, e.g., iPhone Retina PNG screenshots) while leaving small files untouched
  let uploadFile: File = file;
  let cachedCompressedDataUrl = '';
  const COMPRESSION_THRESHOLD_BYTES = 350 * 1024;

  if (file.size > COMPRESSION_THRESHOLD_BYTES && getRemainingMs() > 2000 && !signal?.aborted) {
    try {
      const compressionBudget = Math.min(6000, getRemainingMs());
      cachedCompressedDataUrl = await compressImageForCloud(
        file,
        1280,
        0.82,
        compressionBudget,
        signal
      );
      if (cachedCompressedDataUrl && cachedCompressedDataUrl.startsWith('data:image/')) {
        const baseName = file.name.replace(/\.[^/.]+$/, '') || 'receipt';
        const candidateFile = dataUrlToFile(cachedCompressedDataUrl, `${baseName}.jpg`);
        if (candidateFile && candidateFile.size > 0 && candidateFile.size < file.size) {
          uploadFile = candidateFile;
        }
      }
    } catch (compErr) {
      console.warn('[Receipt Compression Notice]:', compErr);
    }
  }

  if (signal?.aborted || getRemainingMs() <= 0) {
    return {
      success: false,
      uncertain: false,
      error: 'Proses persiapan gambar melewati batas waktu. Silakan coba pilih file lagi.',
    };
  }

  // 4. Generate unique file path with order number and timestamp
  const timestamp = Date.now();
  const safeOrderCode = (orderNumber || `ORD-${timestamp}`).replace(/[^a-zA-Z0-9_-]/g, '');
  const uniqueToken = Math.random().toString(36).substring(2, 8);
  const uploadExt =
    uploadFile.type === 'image/jpeg'
      ? 'jpg'
      : (uploadFile.name.split('.').pop() || ext || 'jpg').toLowerCase();
  const generatedFileName = `${safeOrderCode}_${timestamp}_${uniqueToken}.${uploadExt}`;
  const filePath = `receipts/${generatedFileName}`;
  const uploadContentType =
    uploadFile.type || `image/${uploadExt === 'jpg' ? 'jpeg' : uploadExt}`;

  // 5. Layer 1: Supabase Storage bucket 'leton-images' (up to 35s within 60s total budget)
  const layer1BudgetMs = Math.min(35000, getRemainingMs());
  if (layer1BudgetMs > 1000 && !signal?.aborted) {
    const client = getSupabase();
    const bucketName = 'leton-images';

    try {
      const rawStoragePromise = client.storage.from(bucketName).upload(filePath, uploadFile, {
        cacheControl: '3600',
        upsert: false,
        contentType: uploadContentType,
      });
      const trackedStoragePromise = trackSdkPromise(rawStoragePromise);

      const { data: uploadData, error: uploadError } = await withUploadTimeout(
        trackedStoragePromise,
        layer1BudgetMs,
        'Waktu upload ke penyimpanan utama habis (timeout).',
        signal
      );

      if (!uploadError && uploadData && typeof uploadData.path === 'string' && uploadData.path.trim()) {
        const cleanPath = uploadData.path.trim();
        const { data: publicUrlData } = client.storage.from(bucketName).getPublicUrl(cleanPath);
        if (publicUrlData && typeof publicUrlData.publicUrl === 'string' && publicUrlData.publicUrl.trim()) {
          return {
            success: true,
            url: publicUrlData.publicUrl.trim(),
            path: cleanPath,
          };
        }
      } else if (uploadError) {
        console.warn('[Supabase Storage Notice]:', uploadError.message);
      }
    } catch (err) {
      if (isUploadTimeoutError(err)) {
        // Client timed out waiting for Layer 1. Do NOT assume the server failed to store the file,
        // and do NOT blindly re-upload to Layer 2/3 while Layer 1 outcome is unknown.
        if (!signal?.aborted && getRemainingMs() >= 2500) {
          try {
            const verifyBudgetMs = Math.min(5000, getRemainingMs());
            const { data: existingFiles, error: listErr } = await withUploadTimeout(
              trackSdkPromise(
                client.storage.from(bucketName).list('receipts', {
                  limit: 5,
                  search: generatedFileName,
                })
              ),
              verifyBudgetMs,
              'Verifikasi penyimpanan timeout.',
              signal
            );
            const matched =
              !listErr &&
              Array.isArray(existingFiles) &&
              existingFiles.some((f) => f && f.name === generatedFileName);
            if (matched) {
              const { data: publicUrlData } = client.storage.from(bucketName).getPublicUrl(filePath);
              if (publicUrlData?.publicUrl) {
                return {
                  success: true,
                  url: publicUrlData.publicUrl.trim(),
                  path: filePath,
                };
              }
            }
          } catch {
            // Verification also timed out or failed; outcome remains uncertain
          }
        }

        return {
          success: false,
          uncertain: true,
          error:
            'Waktu tunggu upload habis dan status penyimpanan bukti pembayaran belum dapat dipastikan. Untuk mencegah duplikasi, bukti tidak dikirim ulang otomatis. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
        };
      }
      console.warn('[Supabase Storage Exception]:', err);
    }
  }

  // 6. Layer 2: Backend API /api/upload-receipt (Only runs if Layer 1 definitively failed without timeout)
  const layer2BudgetMs = Math.min(12000, getRemainingMs());
  if (layer2BudgetMs > 1500 && !signal?.aborted) {
    const controller = new AbortController();
    let didLayer2Timeout = false;
    const onCallerAbort = () => controller.abort();
    if (signal) {
      signal.addEventListener('abort', onCallerAbort, { once: true });
    }
    const timeoutId = setTimeout(() => {
      didLayer2Timeout = true;
      controller.abort();
    }, layer2BudgetMs);

    try {
      const formData = new FormData();
      formData.append('receipt', uploadFile);
      formData.append('orderNumber', safeOrderCode);

      const backendEndpoint = getApiUrl('/api/upload-receipt');
      const res = await fetch(backendEndpoint, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      if (res.ok) {
        const json = await res.json();
        if (json && typeof json.url === 'string' && json.url.trim()) {
          const validUrl = json.url.trim();
          const validPath =
            typeof json.path === 'string' && json.path.trim() ? json.path.trim() : validUrl;
          return {
            success: true,
            url: validUrl,
            path: validPath,
          };
        }
      }
    } catch (backendErr) {
      if (didLayer2Timeout || signal?.aborted || isUploadTimeoutError(backendErr)) {
        return {
          success: false,
          uncertain: true,
          error:
            'Waktu tunggu upload ke server habis dan status penyimpanan bukti belum dapat dipastikan. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
        };
      }
      console.warn('[Backend Receipt Upload Notice]:', backendErr);
    } finally {
      clearTimeout(timeoutId);
      if (signal) {
        signal.removeEventListener('abort', onCallerAbort);
      }
    }
  } else if (signal?.aborted || getRemainingMs() <= 1500) {
    return {
      success: false,
      uncertain: true,
      error:
        'Batas waktu upload (60 detik) telah habis dan status penyimpanan bukti belum dapat dipastikan. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
    };
  }

  // 7. Layer 3: Supabase Database Cloud Persistence Fallback (Only runs if Layer 1 & Layer 2 definitively failed)
  const layer3BudgetMs = getRemainingMs();
  if (layer3BudgetMs > 1500 && !signal?.aborted) {
    try {
      let compressedDataUrl = cachedCompressedDataUrl;
      if (!compressedDataUrl && getRemainingMs() > 2500) {
        compressedDataUrl = await compressImageForCloud(
          file,
          1024,
          0.76,
          Math.min(4000, getRemainingMs() - 1500),
          signal
        );
      }

      const remainingForUpsert = getRemainingMs();
      if (compressedDataUrl && compressedDataUrl.startsWith('data:image/') && remainingForUpsert > 1000 && !signal?.aborted) {
        const client = getSupabase();
        const receiptDocId = `receipt_${safeOrderCode}_${timestamp}_${uniqueToken}`;

        const upsertPromise = trackSdkPromise(
          client
            .from('leton_content')
            .upsert(
              {
                id: receiptDocId,
                content: {
                  orderNumber: safeOrderCode,
                  imageDataUrl: compressedDataUrl,
                  uploadedAt: new Date().toISOString(),
                  fileName: file.name,
                },
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'id' }
            )
            .select('id')
            .maybeSingle()
        );

        const { data: upsertData, error: upsertError } = await withUploadTimeout(
          upsertPromise,
          Math.min(10000, remainingForUpsert),
          'Waktu penyimpanan cadangan habis (timeout).',
          signal
        );

        if (!upsertError && upsertData && upsertData.id === receiptDocId) {
          return {
            success: true,
            url: compressedDataUrl,
            path: `supabase://leton_content/${receiptDocId}`,
          };
        } else if (upsertError) {
          console.warn('[Supabase DB Receipt Upsert Error]:', upsertError.message);
        }
      }
    } catch (dbErr) {
      if (isUploadTimeoutError(dbErr) || signal?.aborted) {
        return {
          success: false,
          uncertain: true,
          error:
            'Waktu tunggu upload (60 detik) habis dan status penyimpanan bukti belum dapat dipastikan. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
        };
      }
      console.warn('[Supabase DB Receipt Fallback Note]:', dbErr);
    }
  } else {
    return {
      success: false,
      uncertain: true,
      error:
        'Batas waktu upload (60 detik) telah habis dan status penyimpanan bukti belum dapat dipastikan. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
    };
  }

  return {
    success: false,
    uncertain: false,
    error: 'Gagal mengupload bukti pembayaran. Silakan periksa koneksi internet Anda lalu pilih file lagi.',
  };
}

// Mutex lock to prevent simultaneous duplicate order submissions
const inFlightOrderLocks = new Set<string>();

/**
 * Post Order to Supabase and Backend API with Multi-level Fallback
 */
export async function createNewOrder(
  orderData: CustomerOrder
): Promise<{ success: boolean; order: CustomerOrder; error?: string }> {
  const lockKey = orderData.id || orderData.orderNumber;
  if (inFlightOrderLocks.has(lockKey)) {
    return {
      success: false,
      order: orderData,
      error: 'Pesanan sedang diproses. Harap tunggu konfirmasi.',
    };
  }
  inFlightOrderLocks.add(lockKey);
  try {
    return await executeCreateNewOrder(orderData);
  } finally {
    inFlightOrderLocks.delete(lockKey);
  }
}

/**
 * Helper to record failed background tasks for inspection or retry without interrupting customer UI
 */
export function saveFailedBackgroundJob(jobType: string, payload: any): void {
  try {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem('leton_failed_background_jobs');
    let existing: any[] = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(existing)) existing = [];
    existing.push({
      jobType,
      payload,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem('leton_failed_background_jobs', JSON.stringify(existing.slice(-20)));
  } catch (err) {
    console.warn('Failed to save background job error:', err);
  }
}

/**
 * Automatically process and recover failed background backup jobs when app is reopened
 */
export async function processFailedBackgroundJobs(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem('leton_failed_background_jobs');
    if (!raw) return;
    const jobs: any[] = JSON.parse(raw);
    if (!Array.isArray(jobs) || jobs.length === 0) return;

    const remainingJobs: any[] = [];
    const client = getSupabase();

    for (const job of jobs) {
      if (job.jobType === 'orders_registry_backup' && job.payload?.orderId) {
        try {
          const { data: regRow } = await client.from('leton_content').select('*').eq('id', 'orders_registry').maybeSingle();
          const existingList: CustomerOrder[] = regRow?.content?.orders && Array.isArray(regRow.content.orders) ? regRow.content.orders : [];
          const history = getLocalOrderHistory();
          const targetOrder = history.find(o => o.id === job.payload.orderId || o.orderNumber === job.payload.orderNumber);
          if (targetOrder) {
            const mergedList = [targetOrder, ...existingList.filter((o) => o.id !== targetOrder.id)].slice(0, 500);
            await client.from('leton_content').upsert({
              id: 'orders_registry',
              content: { orders: mergedList },
              updated_at: new Date().toISOString(),
            });
            console.log(`[Background Retry Queue] Recovered orders_registry backup for ${job.payload.orderNumber}`);
            continue;
          }
        } catch (retryErr) {
          console.warn('[Background Retry Queue] Retry failed for orders_registry_backup:', retryErr);
        }
      } else if (job.jobType === 'loyalty_registry_sync' && job.payload?.orderId) {
        try {
          const history = getLocalOrderHistory();
          const targetOrder = history.find((o) => o.id === job.payload.orderId);
          if (targetOrder) {
            const res = await fetch(getApiUrl('/api/orders'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(targetOrder),
            });
            if (res.ok || res.status === 409) {
              console.log(`[Background Retry Queue] Recovered backend loyalty_registry sync for order ${job.payload.orderId}`);
              continue;
            }
          } else {
            continue;
          }
        } catch (retryErr) {
          console.warn('[Background Retry Queue] Retry failed for loyalty_registry_sync:', retryErr);
        }
      }
      remainingJobs.push(job);
    }

    if (remainingJobs.length > 0) {
      localStorage.setItem('leton_failed_background_jobs', JSON.stringify(remainingJobs));
    } else {
      localStorage.removeItem('leton_failed_background_jobs');
    }
  } catch (err) {
    console.warn('[Background Retry Queue] Error processing queue:', err);
  }
}

// In-memory fallback map for guest secrets in case sessionStorage is disabled or throws SecurityError
const inMemoryGuestSecrets = new Map<string, string>();

/**
 * Generates or retrieves a high-entropy guest idempotency secret for a given order ID.
 * Bound to sessionStorage and orderId so retries / refreshes reuse the same secret.
 * Raw secret is NEVER sent across network or logged.
 */
export function getOrCreateGuestIdempotencySecret(orderId: string): string {
  const safeId = String(orderId || '').trim();
  if (!safeId) return '';

  const storageKey = `leton_guest_secret_${safeId}`;

  // Check in-memory fallback first
  if (inMemoryGuestSecrets.has(safeId)) {
    return inMemoryGuestSecrets.get(safeId)!;
  }

  const generate24ByteHex = (): string => {
    const arr = new Uint8Array(24);
    const cryptoObj = typeof window !== 'undefined' ? window.crypto : (globalThis as any).crypto;
    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
      cryptoObj.getRandomValues(arr);
    } else {
      for (let i = 0; i < 24; i++) arr[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  if (typeof window === 'undefined' || !window.sessionStorage) {
    const secret = generate24ByteHex();
    inMemoryGuestSecrets.set(safeId, secret);
    return secret;
  }

  try {
    let secret = window.sessionStorage.getItem(storageKey);
    if (!secret || secret.length < 16) {
      secret = generate24ByteHex();
      window.sessionStorage.setItem(storageKey, secret);
    }
    inMemoryGuestSecrets.set(safeId, secret);
    return secret;
  } catch (err) {
    const secret = generate24ByteHex();
    inMemoryGuestSecrets.set(safeId, secret);
    return secret;
  }
}

/**
 * Pure-JS FIPS 180-4 SHA-256 fallback for environments where Web Crypto subtle.digest is unavailable
 * (e.g. non-HTTPS local network / kiosk webviews). Always outputs a 64-character lowercase hex string.
 */
function sha256PureJsHex(asciiOrUtf8: string): string {
  const utf8Bytes: number[] = [];
  if (typeof TextEncoder !== 'undefined') {
    const encoded = new TextEncoder().encode(asciiOrUtf8);
    for (let i = 0; i < encoded.length; i++) utf8Bytes.push(encoded[i]);
  } else {
    const escaped = unescape(encodeURIComponent(asciiOrUtf8));
    for (let i = 0; i < escaped.length; i++) utf8Bytes.push(escaped.charCodeAt(i));
  }

  const K: number[] = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const bitLen = utf8Bytes.length * 8;
  utf8Bytes.push(0x80);
  while ((utf8Bytes.length % 64) !== 56) {
    utf8Bytes.push(0);
  }

  const highBits = Math.floor(bitLen / 0x100000000);
  const lowBits = bitLen >>> 0;
  utf8Bytes.push((highBits >>> 24) & 0xff, (highBits >>> 16) & 0xff, (highBits >>> 8) & 0xff, highBits & 0xff);
  utf8Bytes.push((lowBits >>> 24) & 0xff, (lowBits >>> 16) & 0xff, (lowBits >>> 8) & 0xff, lowBits & 0xff);

  const w = new Int32Array(64);
  const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));

  for (let i = 0; i < utf8Bytes.length; i += 64) {
    for (let t = 0; t < 16; t++) {
      const idx = i + t * 4;
      w[t] = ((utf8Bytes[idx] << 24) | (utf8Bytes[idx + 1] << 16) | (utf8Bytes[idx + 2] << 8) | utf8Bytes[idx + 3]) | 0;
    }
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(7, w[t - 15]) ^ rotr(18, w[t - 15]) ^ (w[t - 15] >>> 3);
      const s1 = rotr(17, w[t - 2]) ^ rotr(19, w[t - 2]) ^ (w[t - 2] >>> 10);
      w[t] = (w[t - 16] + s0 + w[t - 7] + s1) | 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let t = 0; t < 64; t++) {
      const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[t] + w[t]) | 0;
      const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) | 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) | 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) | 0;
    }

    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
    h4 = (h4 + e) | 0;
    h5 = (h5 + f) | 0;
    h6 = (h6 + g) | 0;
    h7 = (h7 + h) | 0;
  }

  return [h0, h1, h2, h3, h4, h5, h6, h7]
    .map((v) => (v >>> 0).toString(16).padStart(8, '0'))
    .join('')
    .toLowerCase();
}

/**
 * Computes SHA-256 hash formatted as a 64-character lowercase hex string.
 * Uses Web Crypto API when available, with a deterministic pure-JS SHA-256 fallback.
 */
export async function computeSha256Hex(message: string): Promise<string> {
  const safeMsg = String(message || '').trim();
  if (!safeMsg) return '';
  try {
    const cryptoObj = typeof window !== 'undefined' ? window.crypto : (globalThis as any).crypto;
    if (cryptoObj && cryptoObj.subtle && typeof cryptoObj.subtle.digest === 'function') {
      const enc = new TextEncoder();
      const data = enc.encode(safeMsg);
      const hashBuf = await cryptoObj.subtle.digest('SHA-256', data);
      const hashArr = Array.from(new Uint8Array(hashBuf));
      const hex = hashArr.map((b) => b.toString(16).padStart(2, '0')).join('').toLowerCase();
      if (/^[0-9a-f]{64}$/.test(hex)) {
        return hex;
      }
    }
  } catch (err) {
    console.warn('[computeSha256Hex] Subtle crypto error, using pure-JS fallback:', err);
  }
  return sha256PureJsHex(safeMsg);
}

async function executeCreateNewOrder(
  orderData: CustomerOrder
): Promise<{ success: boolean; order: CustomerOrder; error?: string }> {
  const t0_start = performance.now();
  const logs: Array<{ name: string; startTimeMs: number; durationMs: number; status: string }> = [];

  const recordDiagnostic = (name: string, startMs: number, endMs: number, status: string = 'SUCCESS') => {
    logs.push({
      name,
      startTimeMs: Math.round(startMs - t0_start),
      durationMs: Math.round((endMs - startMs) * 100) / 100,
      status,
    });
  };

  // Reject TUNAI / CASH for all new transactions before touching local cache or database
  const normalizedMethod = String(orderData.paymentMethod || '').toUpperCase().trim();
  const normalizedInitialPayStatus = String(orderData.paymentStatus || '').toUpperCase().trim();
  if (
    normalizedMethod === 'TUNAI' ||
    normalizedMethod === 'CASH' ||
    (normalizedMethod !== 'QRIS' && normalizedMethod !== 'DANA') ||
    normalizedInitialPayStatus === 'PAY AT STORE'
  ) {
    return {
      success: false,
      order: orderData,
      error: 'Metode pembayaran tunai (Cash) sudah tidak tersedia untuk pesanan baru. Silakan gunakan QRIS atau DANA.',
    };
  }

  // Always update local cache & history first (Instant < 3ms)
  const t_local_start = performance.now();
  saveOrderToLocalHistory(orderData);
  updateLocalCache(orderData);
  recordDiagnostic('1. Local Storage History & Cache', t_local_start, performance.now(), 'SUCCESS');

  const receiptProofPath = orderData.paymentProofPath || orderData.paymentReceiptPath || orderData.paymentReceiptUrl || null;

  try {
    const client = getSupabase();

    // =========================================================================
    // PRIORITAS 3: PARALELISASI VALIDASI INDEPENDEN (Promise.all)
    // Outlet Availability, Stock Verification, Auth Check, & Customer Session
    // =========================================================================
    const t_parallel_start = performance.now();
    const rawCustomerToken = getCustomerSessionToken();
    const customerToken = rawCustomerToken ? rawCustomerToken.trim().replace(/^["']|["']$/g, '') : null;

    const [outletOpStatus, liveContent, effectiveUserId, sessionCustomerId] = await Promise.all([
      // 1. Outlet availability check
      (async () => {
        try {
          const { fetchOutletsAvailability, normalizeOutletKey, resolveOutletOperatingStatus, getLocalOutletSchedules } = await import('./supabaseOutletStatus');
          const availabilityMap = await fetchOutletsAvailability();
          const normOutlet = normalizeOutletKey(orderData.outletId);
          const schedules = getLocalOutletSchedules();
          return resolveOutletOperatingStatus(normOutlet, schedules, availabilityMap);
        } catch (availErr) {
          console.warn('[Outlet Availability Check Notice]:', availErr);
          return { isOpen: true, reason: undefined }; // Defensive fallback
        }
      })(),

      // 2. Menu stock verification
      (async () => {
        try {
          return await fetchContentFromSupabase();
        } catch (stockErr) {
          console.warn('[Stock Verification Notice]:', stockErr);
          return null;
        }
      })(),

      // 3. Supabase Auth User Check
      (async () => {
        try {
          const { data: { user } } = await client.auth.getUser();
          return user?.id || null;
        } catch {
          return null;
        }
      })(),

      // 4. Customer session token check
      (async () => {
        if (!customerToken) return null;
        try {
          const { data: rawSessionData } = await client.rpc('customer_get_session', {
            p_token: customerToken,
          });
          const sessionData = typeof rawSessionData === 'string' ? JSON.parse(rawSessionData) : rawSessionData;
          return (sessionData && sessionData.success && sessionData.customer?.id) ? sessionData.customer.id : null;
        } catch (sessErr) {
          console.warn('Session verification during order placement notice:', sessErr);
          return null;
        }
      })(),
    ]);

    recordDiagnostic('2. Parallel Pre-Checkout Validations (Promise.all)', t_parallel_start, performance.now(), 'SUCCESS');

    // Check outlet status result
    if (outletOpStatus && !outletOpStatus.isOpen) {
      console.error(`[Order Rejected]: Outlet "${orderData.outletId}" is currently NOT ACCEPTING ORDERS (${outletOpStatus.reason || 'Closed'}).`);
      return {
        success: false,
        order: orderData,
        error: `Cabang ${orderData.outletName || orderData.outletId} sedang TUTUP (${outletOpStatus.reason || 'Tidak menerima pesanan'}). Silakan coba lagi nanti atau pilih cabang lain.`,
      };
    }

    // Check Global Delivery Enabled status and Sudirman outlet restriction when orderType is DELIVERY
    if (String(orderData.orderType || '').toUpperCase().trim() === 'DELIVERY') {
      const normOutlet = String(orderData.outletId || '').toLowerCase().trim();
      const isSudirman = normOutlet === 'sudirman';
      if (!isSudirman) {
        console.error(`[Order Rejected]: Delivery order attempted for non-Sudirman outlet: "${orderData.outletId}"`);
        return {
          success: false,
          order: orderData,
          error: 'Mohon maaf, layanan pengantaran (Delivery) hanya tersedia untuk outlet Sudirman.',
        };
      }

      try {
        const { data: globalDelivRow, error: globalDelivErr } = await client
          .from('leton_content')
          .select('content')
          .eq('id', 'global_delivery_settings')
          .maybeSingle();

        if (!globalDelivErr && globalDelivRow && globalDelivRow.content) {
          const parsedDeliv =
            typeof globalDelivRow.content === 'string'
              ? JSON.parse(globalDelivRow.content)
              : globalDelivRow.content;
          if (parsedDeliv && parsedDeliv.enabled === false) {
            console.warn('[Order Rejected]: Global delivery is currently DISABLED by Super Admin.');
            return {
              success: false,
              order: orderData,
              error: 'Mohon maaf, layanan pengantaran (Delivery) saat ini sedang dinonaktifkan oleh Super Admin. Silakan pilih metode Self Pick-Up.',
            };
          }
        }
      } catch (delivErr) {
        console.warn('[Global Delivery Check Notice]:', delivErr);
      }
    }

    // Check stock status result
    if (liveContent && Array.isArray(liveContent.menuItems)) {
      for (const item of orderData.items) {
        const menuItem = liveContent.menuItems.find(
          (m) => m.id === item.productId || m.name.toLowerCase() === item.name.toLowerCase()
        );
        if (menuItem && !isMenuItemAvailableForOutlet(menuItem, orderData.outletId)) {
          console.error(`[Stock Check Failed]: Menu "${item.name}" is OUT OF STOCK for outlet "${orderData.outletId}"`);
          return {
            success: false,
            order: orderData,
            error: `Pesanan tidak dapat diproses: Menu "${item.name}" sedang HABIS di cabang ${orderData.outletName || orderData.outletId}.`,
          };
        }
      }
    }

    // Validate customer ID resolution
    let validatedCustomerId: string | null = sessionCustomerId;
    if (!validatedCustomerId && typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('leton_customer_profile');
        if (cached) {
          const parsedCached = JSON.parse(cached);
          if (parsedCached?.id) {
            validatedCustomerId = parsedCached.id;
          }
        }
      } catch {}
    }

    const effectiveCustomerId = validatedCustomerId || null;
    orderData.customerId = effectiveCustomerId || undefined;
    orderData.userId = effectiveUserId || undefined;

    const effectivePhone = orderData.customerPhone ? normalizeIndonesianPhone(orderData.customerPhone) : null;
    const originalPaymentStatus = orderData.paymentStatus;

    // =========================================================================
    // SERVER-SIDE AUTHORITATIVE PRICING VERIFICATION (Anti-Price Tampering)
    // =========================================================================
    const t_pricing_start = performance.now();
    let authoritativeTotal = 0;
    const validatedItems = [];
    const menuCatalog = (liveContent && Array.isArray(liveContent.menuItems) && liveContent.menuItems.length > 0)
      ? liveContent.menuItems
      : initialLetonData.menuItems;

    if (!Array.isArray(orderData.items) || orderData.items.length === 0) {
      return {
        success: false,
        order: orderData,
        error: 'Pesanan tidak memiliki item menu.',
      };
    }

    for (const rawItem of orderData.items) {
      const qty = Number(rawItem.quantity);
      if (isNaN(qty) || !Number.isInteger(qty) || qty <= 0 || qty > 100) {
        return {
          success: false,
          order: orderData,
          error: `Kuantitas pesanan tidak valid untuk item ${rawItem.name || 'menu'}.`,
        };
      }

      const catalogMatch = menuCatalog.find(
        (m) => m.id === rawItem.productId || m.name.toLowerCase() === (rawItem.name || '').toLowerCase()
      );

      if (!catalogMatch) {
        return {
          success: false,
          order: orderData,
          error: `Produk "${rawItem.name || 'menu'}" tidak ditemukan di katalog.`,
        };
      }

      if (
        typeof catalogMatch.price !== 'number' ||
        !Number.isFinite(catalogMatch.price) ||
        catalogMatch.price < 0
      ) {
        return {
          success: false,
          order: orderData,
          error: `Harga untuk produk "${catalogMatch.name || rawItem.name || 'menu'}" di katalog tidak valid.`,
        };
      }

      const basePrice = catalogMatch.price;

      let authoritativeSize: ProductSizeOption | undefined = undefined;
      const rawSizeName = rawItem.size?.name ? String(rawItem.size.name).trim() : '';

      if (catalogMatch.hasSize !== false) {
        const masterSizesList = (Array.isArray(liveContent?.masterSizes) && liveContent.masterSizes.length > 0)
          ? liveContent.masterSizes
          : DEFAULT_SIZES;
        let allowedSizes: ProductSizeOption[] = masterSizesList;
        if (Array.isArray(catalogMatch.sizes) && catalogMatch.sizes.length > 0) {
          const isStandardDefaultSizes = catalogMatch.sizes.length === 2 &&
            catalogMatch.sizes.some(s => s.name.toLowerCase() === 'regular') &&
            catalogMatch.sizes.some(s => s.name.toLowerCase() === 'large');
          if (isStandardDefaultSizes && masterSizesList.length > catalogMatch.sizes.length) {
            allowedSizes = masterSizesList;
          } else {
            allowedSizes = catalogMatch.sizes;
          }
        }
        if (!allowedSizes.some(s => s.name.trim().toLowerCase() === 'hot')) {
          allowedSizes = [...allowedSizes, { name: 'Hot', price: 0 }];
        }

        let matchedSize: ProductSizeOption | undefined;
        const STANDARD_SIZE_ALIASES = ['regular', 'reguler', 'standard', 'standar', 'normal'];
        const TEMPERATURE_ALIASES = ['ice', 'cold', 'warm', 'es'];
        const isTemperatureOption = TEMPERATURE_ALIASES.includes(rawSizeName.toLowerCase());

        if (rawSizeName) {
          if (isTemperatureOption) {
            matchedSize = allowedSizes.find((s) => Number(s.price) === 0) || allowedSizes[0] || DEFAULT_SIZE;
            if (!rawItem.customOptions) {
              rawItem.customOptions = [];
            }
            if (!rawItem.customOptions.some((co: any) => String(co.optionName || '').toLowerCase() === rawSizeName.toLowerCase())) {
              rawItem.customOptions.push({
                groupId: 'suhu',
                groupName: 'Suhu',
                optionId: rawSizeName.toLowerCase(),
                optionName: rawSizeName,
                price: 0,
              });
            }
          } else {
            matchedSize = allowedSizes.find(
              (s) => s.name.trim().toLowerCase() === rawSizeName.toLowerCase()
            );

            if (!matchedSize && STANDARD_SIZE_ALIASES.includes(rawSizeName.toLowerCase())) {
              matchedSize = allowedSizes.find(
                (s) => STANDARD_SIZE_ALIASES.includes(s.name.trim().toLowerCase()) || Number(s.price) === 0
              );
            }

            if (!matchedSize) {
              return {
                success: false,
                order: orderData,
                error: `Opsi ukuran "${rawSizeName}" tidak tersedia untuk menu ${catalogMatch.name}. Silakan pilih ukuran yang tersedia di menu.`,
              };
            }
          }
        } else {
          matchedSize = allowedSizes[0] || DEFAULT_SIZE;
        }

        let finalSizeName = matchedSize.name;
        if (STANDARD_SIZE_ALIASES.includes(matchedSize.name.trim().toLowerCase()) && Number(matchedSize.price) === 0) {
          finalSizeName = 'Regular';
        }

        authoritativeSize = {
          name: finalSizeName,
          price: Number(matchedSize.price) || 0,
        };
      }

      const unitPrice = calculateItemUnitPrice(
        basePrice,
        authoritativeSize,
        rawItem.topping,
        rawItem.syrup,
        rawItem.customOptions
      );

      const subtotal = unitPrice * qty;
      authoritativeTotal += subtotal;

      validatedItems.push({
        ...rawItem,
        size: authoritativeSize,
        quantity: qty,
        price: unitPrice,
        subtotal: subtotal,
      });
    }

    orderData.items = validatedItems;
    recordDiagnostic('3. Pricing & Catalog Validation', t_pricing_start, performance.now(), 'SUCCESS');

    // =========================================================================
    // CORE ATOMIC DATABASE TRANSACTION RPC: customer_create_order_with_voucher
    // Inserts order & items, claims voucher, updates loyalty points in PostgreSQL
    // =========================================================================
    const effectiveToken = validatedCustomerId ? customerToken : null;

    if (orderData.voucherId && (!customerToken || !validatedCustomerId)) {
      return {
        success: false,
        order: orderData,
        error: 'Voucher hanya dapat digunakan oleh member yang sedang login dengan sesi aktif.',
      };
    }

    // Resolve or generate high-entropy idempotency secret and compute SHA-256 hex hash (64 hex chars, Anti-IDOR)
    // Raw secret is NEVER sent over the network or persisted in the database.
    let idempotencyHash: string | null = null;
    try {
      const guestSecret = getOrCreateGuestIdempotencySecret(orderData.id);
      if (guestSecret) {
        const computed = await computeSha256Hex(guestSecret);
        if (/^[0-9a-f]{64}$/i.test(computed)) {
          idempotencyHash = computed.toLowerCase();
        }
      }
    } catch (hashErr) {
      console.warn('[Order] Failed to compute idempotency hash:', hashErr);
    }

    const customerNoteWithPickup = (orderData.customerNote || orderData.pickupTime || orderData.pickup_time)
      ? `${orderData.customerNote || ''}${(orderData.pickupTime || orderData.pickup_time) && !orderData.customerNote?.includes(orderData.pickupTime || orderData.pickup_time) ? ` [Pickup: ${orderData.pickupTime || orderData.pickup_time}]` : ''}`.trim() || null
      : null;

    const rpcParams: Record<string, any> = {
      p_token: effectiveToken,
      p_order_id: orderData.id,
      p_order_number: orderData.orderNumber,
      p_outlet_id: orderData.outletId,
      p_outlet_name: orderData.outletName,
      p_customer_name: orderData.customerName,
      p_customer_phone: effectivePhone,
      p_order_type: orderData.orderType,
      p_table_number: orderData.tableNumber || null,
      p_items: validatedItems,
      p_payment_method: orderData.paymentMethod,
      p_payment_status: originalPaymentStatus,
      p_payment_proof_path: receiptProofPath,
      p_payment_receipt_url: orderData.paymentReceiptUrl || null,
      p_customer_note: customerNoteWithPickup,
      p_voucher_id: orderData.voucherId || null,
      p_idempotency_hash: idempotencyHash,
    };

    if (orderData.orderType === 'DELIVERY') {
      rpcParams.p_delivery_address = (orderData.deliveryAddress || orderData.delivery_address || '').trim() || null;
      rpcParams.p_customer_lat =
        orderData.customerLat !== undefined && orderData.customerLat !== null
          ? Number(orderData.customerLat)
          : (orderData.customer_lat !== undefined && orderData.customer_lat !== null ? Number(orderData.customer_lat) : null);
      rpcParams.p_customer_lng =
        orderData.customerLng !== undefined && orderData.customerLng !== null
          ? Number(orderData.customerLng)
          : (orderData.customer_lng !== undefined && orderData.customer_lng !== null ? Number(orderData.customer_lng) : null);
      rpcParams.p_delivery_distance =
        orderData.deliveryDistance !== undefined && orderData.deliveryDistance !== null
          ? Number(orderData.deliveryDistance)
          : (orderData.delivery_distance !== undefined && orderData.delivery_distance !== null ? Number(orderData.delivery_distance) : null);
      rpcParams.p_delivery_fee =
        orderData.deliveryFee !== undefined && orderData.deliveryFee !== null
          ? Number(orderData.deliveryFee)
          : (orderData.delivery_fee !== undefined && orderData.delivery_fee !== null ? Number(orderData.delivery_fee) : null);
    }

    const t_rpc_start = performance.now();
    const { data: rawRpcResult, error: rpcError } = await client.rpc(
      'customer_create_order_with_voucher',
      rpcParams
    );

    if (rpcError) {
      recordDiagnostic('4. Database Order & Items INSERT (RPC customer_create_order_with_voucher)', t_rpc_start, performance.now(), 'FAILED');
      console.error('[Order] Universal RPC customer_create_order_with_voucher error:', rpcError);

      const isMissingRpcSignature =
        orderData.orderType === 'DELIVERY' &&
        (rpcError.message?.includes('schema cache') ||
         rpcError.message?.includes('function') ||
         rpcError.code === 'PGRST202');

      const userErrorMessage = isMissingRpcSignature
        ? 'Layanan pemesanan Delivery sedang dalam pembaruan sistem database staging. Silakan pilih opsi Pick Up atau hubungi kasir.'
        : (rpcError.message || 'Gagal memproses pesanan ke database. Silakan coba lagi.');

      // Record error log asynchronously
      (async () => {
        try {
          const { data: regRow } = await client.from('leton_content').select('*').eq('id', 'orders_registry').maybeSingle();
          const existingList: CustomerOrder[] = regRow?.content?.orders && Array.isArray(regRow.content.orders) ? regRow.content.orders : [];
          const cleanedList = existingList.filter((o) => o.id !== orderData.id);
          await client.from('leton_content').upsert({
            id: 'orders_registry',
            content: {
              ...regRow?.content,
              orders: cleanedList,
              last_error: { order_id: orderData.id, order_number: orderData.orderNumber, error: rpcError.message || 'RPC Execution Failed', timestamp: new Date().toISOString() },
            },
            updated_at: new Date().toISOString(),
          });
        } catch {}
      })();

      return {
        success: false,
        order: orderData,
        error: userErrorMessage,
      };
    }

    recordDiagnostic('4. Database Order & Items INSERT (RPC customer_create_order_with_voucher)', t_rpc_start, performance.now(), 'SUCCESS');

    let rpcResult: any = rawRpcResult;
    if (typeof rawRpcResult === 'string') {
      try {
        rpcResult = JSON.parse(rawRpcResult);
      } catch (parseErr) {
        console.warn('[Order] Failed to JSON.parse rawRpcResult:', parseErr);
      }
    }

    if (!rpcResult || !rpcResult.success) {
      console.error('[Order] Universal RPC returned unsuccess:', rpcResult);
      return {
        success: false,
        order: orderData,
        error: rpcResult?.error || 'Gagal memproses pesanan.',
      };
    }

    console.log('[Order] Universal RPC customer_create_order_with_voucher SUCCESS:', rpcResult);

    const confirmedItems =
      Array.isArray(rpcResult.items) && rpcResult.items.length > 0
        ? rpcResult.items
        : validatedItems;

    const rpcTotalRaw =
      rpcResult.total_amount !== undefined && rpcResult.total_amount !== null
        ? Number(rpcResult.total_amount)
        : NaN;

    if (!Number.isFinite(rpcTotalRaw) || rpcTotalRaw < 0 || !Array.isArray(confirmedItems) || confirmedItems.length === 0) {
      return {
        success: false,
        order: orderData,
        error: 'Gagal mengonfirmasi total harga dan rincian pesanan dari server. Silakan coba lagi.',
      };
    }

    orderData.items = confirmedItems;

    if (rpcResult.discount_amount !== undefined && rpcResult.discount_amount !== null) {
      orderData.discountAmount = Number(rpcResult.discount_amount);
      orderData.discount_amount = Number(rpcResult.discount_amount);
    } else if (!orderData.voucherId) {
      orderData.discountAmount = 0;
      orderData.discount_amount = 0;
    }

    if (rpcResult.delivery_fee !== undefined && rpcResult.delivery_fee !== null) {
      const confirmedDeliveryFee = Number(rpcResult.delivery_fee);
      if (Number.isFinite(confirmedDeliveryFee) && confirmedDeliveryFee >= 0) {
        orderData.deliveryFee = confirmedDeliveryFee;
        (orderData as any).delivery_fee = confirmedDeliveryFee;
      }
    }

    // Authoritative Service Fee (Rp1.500 per transaction, idempotent)
    const verifiedItemsSubtotal = confirmedItems.reduce(
      (acc: number, it: any) => acc + ((Number(it.unitPrice ?? it.price ?? 0)) * (Number(it.quantity || 1))),
      0
    );
    const authoritativeBreakdown = calculateAuthoritativeOrderTotal({
      subtotal: verifiedItemsSubtotal,
      discountAmount: Number(orderData.discountAmount || 0),
      orderType: orderData.orderType,
      deliveryFee: Number(orderData.deliveryFee ?? (orderData as any).delivery_fee ?? 0),
    });

    orderData.serviceFee = authoritativeBreakdown.serviceFee;
    (orderData as any).service_fee = authoritativeBreakdown.serviceFee;
    orderData.totalAmount = authoritativeBreakdown.totalAmount;

    if (rpcResult.delivery_distance !== undefined && rpcResult.delivery_distance !== null) {
      const confirmedDeliveryDistance = Number(rpcResult.delivery_distance);
      if (Number.isFinite(confirmedDeliveryDistance) && confirmedDeliveryDistance >= 0) {
        orderData.deliveryDistance = confirmedDeliveryDistance;
        (orderData as any).delivery_distance = confirmedDeliveryDistance;
      }
    }

    if (rpcResult.voucher_id) {
      orderData.voucherId = rpcResult.voucher_id;
    } else if (!orderData.voucherId) {
      orderData.voucherId = undefined;
    }

    if (rpcResult.voucher_code) {
      orderData.voucherCode = rpcResult.voucher_code;
    } else if (!orderData.voucherId) {
      orderData.voucherCode = undefined;
    }

    if (rpcResult.order_number) {
      orderData.orderNumber = rpcResult.order_number;
    }
    if (rpcResult.order_id) {
      orderData.id = rpcResult.order_id;
    }
    if (rpcResult.customer_id) {
      orderData.customerId = rpcResult.customer_id;
    }
    if (rpcResult.user_id) {
      orderData.userId = rpcResult.user_id;
    } else {
      orderData.userId = effectiveUserId || undefined;
    }

    // Reconcile total_amount and sync secondary loyalty_registry via authoritative backend (/api/orders)
    // Never call getSupabase('super_admin') from the browser for privileged reconciliation or registry writes.
    const needsBackendReconciliation =
      Math.abs(rpcTotalRaw - authoritativeBreakdown.totalAmount) > 1 && Boolean(orderData.id);

    const syncOrderWithBackendServer = async (): Promise<{ ok: boolean; reconciledTotal?: number; error?: string }> => {
      const backendUrl = getApiUrl('/api/orders');
      let lastErr = '';
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const res = await fetch(backendUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          const json = await res.json().catch(() => null);
          if (res.ok && json && json.success) {
            const serverTotal = Number(json.order?.totalAmount ?? json.order?.total_amount ?? NaN);
            return {
              ok: true,
              reconciledTotal: Number.isFinite(serverTotal) ? serverTotal : undefined,
            };
          }
          lastErr = json?.error || `HTTP ${res.status}`;
          if (res.status >= 400 && res.status < 500) {
            break;
          }
        } catch (err: any) {
          lastErr = err?.message || 'Network error';
          if (attempt < 3) await new Promise((r) => setTimeout(r, 800));
        }
      }
      return { ok: false, error: lastErr };
    };

    if (needsBackendReconciliation) {
      const t_reconcile_start = performance.now();
      const reconcileRes = await syncOrderWithBackendServer();
      if (
        !reconcileRes.ok ||
        (reconcileRes.reconciledTotal !== undefined &&
          Math.abs(reconcileRes.reconciledTotal - authoritativeBreakdown.totalAmount) > 1)
      ) {
        recordDiagnostic('5. Backend Authoritative Total Reconciliation', t_reconcile_start, performance.now(), 'FAILED');
        return {
          success: false,
          order: orderData,
          error:
            reconcileRes.error ||
            'Gagal menyinkronkan rincian biaya layanan pesanan dengan server. Silakan coba lagi.',
        };
      }
      recordDiagnostic('5. Backend Authoritative Total Reconciliation', t_reconcile_start, performance.now(), 'SUCCESS');
    } else {
      // RPC already persisted authoritative total_amount including Rp1.500 service_fee.
      // Dispatch non-blocking backend sync for push notifications & server-side loyalty_registry sync.
      syncOrderWithBackendServer().then((res) => {
        if (!res.ok && orderData.voucherId) {
          saveFailedBackgroundJob('loyalty_registry_sync', {
            orderId: orderData.id,
            voucherId: orderData.voucherId,
            error: res.error || 'Backend sync failed',
          });
        }
      });
      recordDiagnostic('5. Backend Order & Loyalty Sync', performance.now(), performance.now(), 'BACKGROUND_DISPATCHED');
    }

    // Refresh local history and cache immediately with server-confirmed order values
    saveOrderToLocalHistory(orderData);
    updateLocalCache(orderData);

    // =========================================================================
    // PRIORITAS 1: PENCADANGAN RIWAYAT UTAMA leton_content DI BACKGROUND (NON-BLOCKING)
    // Pesanan sudah 100% tersimpan sah di public.orders & public.order_items.
    // =========================================================================
    const backupOrdersRegistryInBackground = async () => {
      try {
        const clientDb = getSupabase();
        const { data: regRow } = await clientDb
          .from('leton_content')
          .select('*')
          .eq('id', 'orders_registry')
          .maybeSingle();

        const existingList: CustomerOrder[] =
          regRow?.content?.orders && Array.isArray(regRow.content.orders)
            ? regRow.content.orders
            : [];

        const mergedList = [orderData, ...existingList.filter((o) => o.id !== orderData.id)].slice(0, 500);

        await clientDb.from('leton_content').upsert({
          id: 'orders_registry',
          content: { orders: mergedList },
          updated_at: new Date().toISOString(),
        });
        console.log(`[Background Task] Orders registry backup succeeded for ${orderData.orderNumber}`);
      } catch (backupErr) {
        console.warn('[Background Task Notice] Orders registry backup failed (logged to retry queue):', backupErr);
        saveFailedBackgroundJob('orders_registry_backup', {
          orderId: orderData.id,
          orderNumber: orderData.orderNumber,
          error: String(backupErr),
        });
      }
    };
    backupOrdersRegistryInBackground();
    recordDiagnostic('6. Orders Registry Backup Upsert', performance.now(), performance.now(), 'BACKGROUND_DISPATCHED');

    // Instant Realtime Broadcast (Non-blocking)
    if (orderData.paymentMethod !== 'DANA' || orderData.paymentStatus === 'PAID') {
      try {
        const broadcastChannel = client.channel('leton_orders_stream');
        broadcastChannel.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            broadcastChannel.send({
              type: 'broadcast',
              event: 'ORDER_CREATED',
              payload: orderData,
            });
          }
        });
      } catch {}
    }

  } catch (err: any) {
    console.error('[Supabase Orders Exception]:', err);
    return {
      success: false,
      order: orderData,
      error: `Terjadi kendala saat memproses pesanan: ${err?.message || 'Silakan coba lagi.'}`,
    };
  }

  const t0_end = performance.now();
  const totalDurationMs = Math.round((t0_end - t0_start) * 100) / 100;

  console.group(`[CHECKOUT OPTIMIZED BENCHMARK] Total blocking latency: ${totalDurationMs} ms`);
  console.table(logs);
  console.groupEnd();

  return {
    success: true,
    order: orderData,
  };
}

/**
 * Helper to cross-reference orders_registry in leton_content
 * Restores customOptions, unitPrices, and totalAmounts if public.orders lost them or had mismatch
 */
async function enrichOrdersFromRegistry(orders: CustomerOrder[]): Promise<CustomerOrder[]> {
  if (!orders || orders.length === 0) return orders;
  try {
    const client = getSupabase();
    const { data: regRow } = await client
      .from('leton_content')
      .select('*')
      .eq('id', 'orders_registry')
      .maybeSingle();

    const registryOrders: CustomerOrder[] =
      regRow?.content?.orders && Array.isArray(regRow.content.orders)
        ? regRow.content.orders
        : [];

    if (!registryOrders.length) return orders;

    const regMap = new Map<string, CustomerOrder>();
    registryOrders.forEach((ro) => {
      if (ro.id) regMap.set(ro.id, ro);
      if (ro.orderNumber) {
        regMap.set(ro.orderNumber, ro);
        regMap.set(ro.orderNumber.replace(/^#/, ''), ro);
      }
    });

    return orders.map((ord) => {
      const match = regMap.get(ord.id) || regMap.get(ord.orderNumber) || regMap.get(ord.orderNumber.replace(/^#/, ''));
      if (!match) return ord;

      const mergedItems = Array.isArray(match.items) && match.items.length > 0
        ? ord.items.map((it, idx) => {
            const matchItem = match.items[idx] || match.items.find((m) => m.productId === it.productId || m.name === it.name);
            const customOptions = (it.customOptions && it.customOptions.length > 0)
              ? it.customOptions
              : matchItem?.customOptions;

            const size = it.size || matchItem?.size;
            const topping = it.topping || matchItem?.topping;
            const syrup = it.syrup || matchItem?.syrup;

            const existingUnit = Number(it.unitPrice ?? matchItem?.unitPrice ?? it.price ?? matchItem?.price ?? 0);
            const basePrice = Number((it as any).basePrice ?? (matchItem as any)?.basePrice ?? (it as any).product?.price ?? (matchItem as any)?.product?.price ?? 0);

            const unitPrice = existingUnit > 0
              ? existingUnit
              : (basePrice > 0
                  ? calculateItemUnitPrice(basePrice, size, topping, syrup, customOptions)
                  : 0);

            return {
              ...it,
              size,
              topping,
              syrup,
              customOptions,
              price: unitPrice,
              unitPrice,
            };
          })
        : ord.items;

      const resolvedOrderType = ord.orderType || match.orderType || 'DINE IN';
      const isDelivery = String(resolvedOrderType).toUpperCase().trim() === 'DELIVERY';

      const resolvedDeliveryAddress =
        ord.deliveryAddress || ord.delivery_address || match.deliveryAddress || match.delivery_address || undefined;
      const resolvedCustomerLat =
        ord.customerLat ?? ord.customer_lat ?? match.customerLat ?? match.customer_lat ?? undefined;
      const resolvedCustomerLng =
        ord.customerLng ?? ord.customer_lng ?? match.customerLng ?? match.customer_lng ?? undefined;
      const resolvedDeliveryDistance =
        ord.deliveryDistance ?? ord.delivery_distance ?? match.deliveryDistance ?? match.delivery_distance ?? undefined;

      const ordDeliveryFee = Number(ord.deliveryFee ?? ord.delivery_fee ?? 0);
      const matchDeliveryFee = Number(match.deliveryFee ?? match.delivery_fee ?? 0);
      const resolvedDeliveryFee = isDelivery
        ? (ordDeliveryFee > 0 ? ordDeliveryFee : (matchDeliveryFee > 0 ? matchDeliveryFee : 0))
        : undefined;

      const resolvedDeliveryStatus =
        ord.deliveryStatus || ord.delivery_status || match.deliveryStatus || match.delivery_status || (isDelivery ? 'UNASSIGNED' : undefined);
      const resolvedCourierUsername =
        ord.courierUsername || ord.courier_username || match.courierUsername || match.courier_username || undefined;
      const resolvedPickupTime =
        ord.pickupTime || ord.pickup_time || match.pickupTime || match.pickup_time || undefined;

      const calculatedSubtotal = mergedItems.reduce((acc, item) => acc + ((item.unitPrice || item.price || 0) * (item.quantity || 1)), 0);
      const discount = Number(ord.discountAmount ?? ord.discount_amount ?? match.discountAmount ?? match.discount_amount ?? 0);
      const delivery = isDelivery ? (resolvedDeliveryFee || 0) : 0;
      const dbTotal = Number(ord.totalAmount || 0);
      const resolvedServiceFee = resolvePersistedOrderServiceFee({
        ...ord,
        items: mergedItems,
        discountAmount: discount,
        deliveryFee: delivery,
        serviceFee: ord.serviceFee ?? ord.service_fee ?? match.serviceFee ?? match.service_fee ?? null,
      });

      const recalculatedTotal = calculatedSubtotal > 0
        ? Math.max(0, calculatedSubtotal - discount) + delivery + resolvedServiceFee
        : dbTotal;

      // Preserve the authoritative total_amount from public.orders whenever it is valid (> 0)
      // so historical transactions are never retroactively inflated by Rp1.500.
      const finalTotal = dbTotal > 0
        ? dbTotal
        : (Number(match.totalAmount || 0) > 0 ? Number(match.totalAmount) : recalculatedTotal);

      return {
        ...ord,
        orderType: resolvedOrderType,
        items: mergedItems,
        totalAmount: finalTotal,
        discountAmount: discount,
        discount_amount: discount,
        serviceFee: resolvedServiceFee,
        service_fee: resolvedServiceFee,
        deliveryAddress: resolvedDeliveryAddress,
        delivery_address: resolvedDeliveryAddress,
        customerLat: resolvedCustomerLat,
        customer_lat: resolvedCustomerLat,
        customerLng: resolvedCustomerLng,
        customer_lng: resolvedCustomerLng,
        deliveryDistance: resolvedDeliveryDistance,
        delivery_distance: resolvedDeliveryDistance,
        deliveryFee: resolvedDeliveryFee,
        delivery_fee: resolvedDeliveryFee,
        deliveryStatus: resolvedDeliveryStatus,
        delivery_status: resolvedDeliveryStatus,
        courierUsername: resolvedCourierUsername,
        courier_username: resolvedCourierUsername,
        pickupTime: resolvedPickupTime,
        pickup_time: resolvedPickupTime,
      };
    });
  } catch (err) {
    console.warn('[Enrich Orders Warning]:', err);
    return orders;
  }
}

/**
 * Fetch all orders for Admin Dashboard & Kitchen Display
 * Supabase database 'orders' table is the SINGLE SOURCE OF TRUTH.
 * If targetOutletId is specified and not 'ALL', strictly filters orders for that outlet.
 */
export async function fetchAllOrders(targetOutletId?: string): Promise<CustomerOrder[]> {
  const activeRole = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') || '' : '';
  const activeOutlet = targetOutletId || (typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '');
  const filterId = activeOutlet && activeOutlet !== 'ALL' ? activeOutlet : undefined;

  // 1. Primary & Authoritative: Supabase Database 'orders' table
  try {
    const client = getSupabase(activeRole, filterId);
    const { data, error } = await client
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(300);

    if (!error && Array.isArray(data)) {
      const orders: CustomerOrder[] = data.map((row: any) => ({
        id: row.id,
        orderNumber: row.order_number || row.orderNumber || 'LTN-????',
        outletId: row.outlet_id || row.outletId || '',
        outletName: row.outlet_name || row.outletName || '',
        customerName: row.customer_name || row.customerName || '',
        customerPhone: row.customer_phone || row.customerPhone || '',
        customerId: row.customer_id || row.customerId || undefined,
        userId: row.user_id || row.userId || undefined,
        orderType: row.order_type || row.orderType || 'DINE IN',
        tableNumber: row.table_number || row.tableNumber || '',
        items: Array.isArray(row.items) ? row.items : [],
        totalAmount: Number(row.total_amount || row.totalAmount || 0),
        paymentMethod: row.payment_method || row.paymentMethod || 'QRIS',
        paymentStatus: row.payment_status || row.paymentStatus || 'WAITING PAYMENT',
        paymentProofPath: row.payment_proof_path || row.payment_receipt_path || row.paymentReceiptPath,
        paymentReceiptUrl: row.payment_receipt_url || row.paymentReceiptUrl,
        paymentReceiptPath: row.payment_receipt_path || row.payment_proof_path || row.paymentReceiptPath,
        rejectionReason: row.rejection_reason || row.rejectionReason,
        orderStatus: row.order_status || row.orderStatus || 'NEW',
        customerNote: row.customer_note || row.customerNote || '',
        voucherId: row.voucher_id || row.voucherId || undefined,
        voucherCode: row.voucher_code || row.voucherCode || undefined,
        discountAmount: row.discount_amount !== undefined && row.discount_amount !== null ? Number(row.discount_amount) : (row.discountAmount !== undefined ? Number(row.discountAmount) : 0),
        discount_amount: row.discount_amount !== undefined && row.discount_amount !== null ? Number(row.discount_amount) : (row.discountAmount !== undefined ? Number(row.discountAmount) : 0),
        pickupTime: row.pickup_time || row.pickupTime || undefined,
        pickup_time: row.pickup_time || row.pickupTime || undefined,
        deliveryAddress: row.delivery_address || row.deliveryAddress || undefined,
        delivery_address: row.delivery_address || row.deliveryAddress || undefined,
        customerLat: row.customer_lat !== undefined && row.customer_lat !== null ? Number(row.customer_lat) : (row.customerLat !== undefined && row.customerLat !== null ? Number(row.customerLat) : undefined),
        customer_lat: row.customer_lat !== undefined && row.customer_lat !== null ? Number(row.customer_lat) : (row.customerLat !== undefined && row.customerLat !== null ? Number(row.customerLat) : undefined),
        customerLng: row.customer_lng !== undefined && row.customer_lng !== null ? Number(row.customer_lng) : (row.customerLng !== undefined && row.customerLng !== null ? Number(row.customerLng) : undefined),
        customer_lng: row.customer_lng !== undefined && row.customer_lng !== null ? Number(row.customer_lng) : (row.customerLng !== undefined && row.customerLng !== null ? Number(row.customerLng) : undefined),
        deliveryDistance: row.delivery_distance !== undefined && row.delivery_distance !== null ? Number(row.delivery_distance) : (row.deliveryDistance !== undefined && row.deliveryDistance !== null ? Number(row.deliveryDistance) : undefined),
        delivery_distance: row.delivery_distance !== undefined && row.delivery_distance !== null ? Number(row.delivery_distance) : (row.deliveryDistance !== undefined && row.deliveryDistance !== null ? Number(row.deliveryDistance) : undefined),
        deliveryFee: row.delivery_fee !== undefined && row.delivery_fee !== null ? Number(row.delivery_fee) : (row.deliveryFee !== undefined && row.deliveryFee !== null ? Number(row.deliveryFee) : undefined),
        delivery_fee: row.delivery_fee !== undefined && row.delivery_fee !== null ? Number(row.delivery_fee) : (row.deliveryFee !== undefined && row.deliveryFee !== null ? Number(row.deliveryFee) : undefined),
        serviceFee: resolvePersistedOrderServiceFee(row),
        service_fee: resolvePersistedOrderServiceFee(row),
        deliveryStatus: row.delivery_status || row.deliveryStatus || undefined,
        delivery_status: row.delivery_status || row.deliveryStatus || undefined,
        courierUsername: row.courier_username || row.courierUsername || undefined,
        courier_username: row.courier_username || row.courierUsername || undefined,
        createdAt: row.created_at || row.createdAt || new Date().toISOString(),
        updatedAt: row.updated_at || row.updatedAt,
      }));

      // Apply strict outlet filtering if targetOutletId is specified
      const filtered = filterId
        ? orders.filter((o) => matchesOutlet(o.outletId, filterId))
        : orders;

      // Enrich orders with custom options backup from registry
      const enriched = await enrichOrdersFromRegistry(filtered);

      // Update local storage cache to strictly match the authoritative database state
      safeSetItem(ADMIN_ORDERS_CACHE_KEY, JSON.stringify(stripHeavyBase64Images(enriched).slice(0, 50)));

      return enriched;
    }
  } catch (err) {
    console.warn('[Fetch Supabase Orders Warning]:', err);
  }

  // 2. Offline Fallback ONLY if network/database connection is completely unreachable
  try {
    const cached = safeGetItem(ADMIN_ORDERS_CACHE_KEY);
    if (cached) {
      const parsed: CustomerOrder[] = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return filterId ? parsed.filter((o) => matchesOutlet(o.outletId, filterId)) : parsed;
      }
    }
  } catch {}

  return [];
}

/**
 * Update Order Status and/or Payment Status (Verified / Rejected / Completed)
 * Designed for non-blocking execution & instant optimistic admin feedback.
 */
export async function updateOrderStatus(
  orderId: string,
  newOrderStatus: OrderStatus,
  newPaymentStatus?: PaymentStatus,
  rejectionReason?: string,
  fullOrderData?: CustomerOrder
): Promise<boolean> {
  const updatePayload: any = {
    order_status: newOrderStatus,
    updated_at: new Date().toISOString(),
  };
  if (newPaymentStatus) {
    updatePayload.payment_status = newPaymentStatus;
  }
  if (newPaymentStatus === 'PAID' || newPaymentStatus === 'PAYMENT REJECTED' || newPaymentStatus === 'REJECTED' || newOrderStatus === 'CANCELLED') {
    updatePayload.payment_verified_at = new Date().toISOString();
  }
  if (rejectionReason !== undefined) {
    updatePayload.rejection_reason = rejectionReason;
  }

  // 1. Direct Supabase update in 'orders' table (Primary authoritative database record)
  let updateSuccess = false;
  let lastDbError: string | null = null;

  try {
    const role = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') || '' : '';
    const rawOutlet = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '';
    const activeOutlet = normalizeOutletKey(rawOutlet);
    const cleanNumber = orderId.replace(/^#/, '').trim();

    // Authenticate update call with admin session authorization context (super_admin or normalized activeOutlet)
    const client = role === 'super_admin'
      ? getSupabase('super_admin')
      : getSupabase(role || 'outlet_admin', activeOutlet);

    // Fetch the order first to discover exact ID
    const { data: ordList } = await client
      .from('orders')
      .select('id, outlet_id, order_number')
      .or(`id.eq.${orderId},id.eq.${cleanNumber},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`)
      .limit(1);

    const actualDbId = ordList && ordList[0]?.id ? ordList[0].id : orderId;

    let { data, error } = await client
      .from('orders')
      .update(updatePayload)
      .or(`id.eq.${actualDbId},id.eq.${orderId},id.eq.${cleanNumber},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`)
      .select();

    if (error && error.message.includes('payment_verified_at')) {
      delete updatePayload.payment_verified_at;
      const retry = await client
        .from('orders')
        .update(updatePayload)
        .or(`id.eq.${actualDbId},id.eq.${orderId},id.eq.${cleanNumber},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`)
        .select();
      error = retry.error;
      data = retry.data;
    }

    if (error) {
      lastDbError = error.message;
      console.warn('[Supabase updateOrderStatus direct notice]:', error.message);
    } else if (data && data.length > 0) {
      updateSuccess = true;
    }

    // Broadcast ORDER_STATUS_UPDATED event on Supabase Realtime channel for instant customer notification
    try {
      const liveChannel = client.channel(`order_live_${orderId}`);
      liveChannel.send({
        type: 'broadcast',
        event: 'ORDER_STATUS_UPDATED',
        payload: {
          order_id: orderId,
          order_status: newOrderStatus,
          payment_status: newPaymentStatus,
          rejection_reason: rejectionReason,
          updated_at: updatePayload.updated_at,
        },
      });
    } catch (bcErr) {
      console.warn('[Supabase Broadcast Warning]:', bcErr);
    }

    // Award loyalty points & referral rewards ONLY when Admin marks Order as READY ("SIAP")
    if (newOrderStatus === 'READY') {
      try {
        // Resolve target order: prioritize fullOrderData, then fetchSingleOrder, then orders_registry
        let targetOrder: CustomerOrder | null = fullOrderData || null;
        if (!targetOrder) {
          targetOrder = await fetchSingleOrder(orderId);
        }
        if (!targetOrder) {
          try {
            const { data: regRow } = await client
              .from('leton_content')
              .select('content')
              .eq('id', 'orders_registry')
              .maybeSingle();
            if (regRow?.content?.orders && Array.isArray(regRow.content.orders)) {
              targetOrder =
                regRow.content.orders.find(
                  (o: any) =>
                    o.id === actualDbId ||
                    o.id === orderId ||
                    o.orderNumber === cleanNumber ||
                    o.orderNumber === `#${cleanNumber}`
                ) || null;
            }
          } catch (rErr) {
            console.warn('[Orders registry lookup notice]:', rErr);
          }
        }

        // Canonical processOrderPointsEarning execution (handles points, referral reward, loyalty_registry, adjust_customer_points)
        if (targetOrder && (targetOrder.customerId || targetOrder.userId)) {
          const readyOrder: CustomerOrder = {
            ...targetOrder,
            id: actualDbId || targetOrder.id || orderId,
            orderStatus: 'READY',
            paymentStatus: newPaymentStatus || targetOrder.paymentStatus,
          };
          await processOrderPointsEarning(readyOrder);
        } else {
          // Fallback if targetOrder could not be resolved in client memory: trigger DB RPC directly
          try {
            await client.rpc('process_order_points_earning', { p_order_id: actualDbId || orderId });
          } catch (rpcErr) {
            console.warn('[Auxiliary process_order_points_earning RPC notice]:', rpcErr);
          }
        }
      } catch (ptsErr) {
        console.warn('[Order Points Earning Error in updateOrderStatus]:', ptsErr);
      }
    }
  } catch (err: any) {
    console.warn('[Supabase direct update error]:', err?.message || err);
    lastDbError = err?.message || 'Direct Supabase update error';
  }

  // 2. Server API fallback / verification (ONLY executed if direct Supabase DB update failed)
  if (!updateSuccess) {
    try {
      const adminRole = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') || '' : '';
      const adminOutlet = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '';
      const adminToken = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';

      const serverRes = await fetch(getApiUrl(`/api/orders/${encodeURIComponent(orderId)}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
          'x-admin-role': adminRole || 'super_admin',
          'x-outlet-id': normalizeOutletKey(adminOutlet),
        },
        body: JSON.stringify({
          orderStatus: newOrderStatus,
          paymentStatus: newPaymentStatus,
          rejectionReason: rejectionReason,
        }),
      });

      if (serverRes.ok) {
        const serverJson = await serverRes.json();
        if (serverJson && (serverJson.success || serverJson.order)) {
          updateSuccess = true;
        }
      }
    } catch (apiErr: any) {
      console.warn('[Server updateOrderStatus API Warning]:', apiErr?.message || apiErr);
    }
  }

  if (!updateSuccess) {
    throw new Error(lastDbError || 'Gagal memperbarui status pesanan. Pastikan akun admin memiliki akses wewenang ke outlet ini.');
  }

  // 2. Parallel background sync for registry & local cache without blocking caller
  (async () => {
    try {
      // Local storage cache immediate sync
      const cached = safeGetItem(ADMIN_ORDERS_CACHE_KEY);
      if (cached) {
        let list: CustomerOrder[] = JSON.parse(cached);
        list = list.map((o) => {
          if (o.id === orderId) {
            return {
              ...o,
              orderStatus: newOrderStatus,
              paymentStatus: newPaymentStatus || o.paymentStatus,
              rejectionReason: rejectionReason !== undefined ? rejectionReason : o.rejectionReason,
              updatedAt: new Date().toISOString(),
            };
          }
          return o;
        });
        safeSetItem(ADMIN_ORDERS_CACHE_KEY, JSON.stringify(stripHeavyBase64Images(list).slice(0, 50)));
      }

      // Backend API sync
      const adminRole = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') || '' : '';
      const adminOutlet = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '';
      const adminToken = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
      fetch(getApiUrl(`/api/orders/${orderId}`), {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
          'x-admin-role': adminRole,
          'x-outlet-id': adminOutlet
        },
        body: JSON.stringify({
          orderStatus: newOrderStatus,
          paymentStatus: newPaymentStatus,
          rejectionReason,
        }),
      }).catch(() => {});
    } catch {
      // ignore
    }
  })();

  return true;
}

export interface DeleteOrderResult {
  success: boolean;
  deleted: boolean;
  verified: boolean;
  isArchived?: boolean;
  message?: string;
  error?: string;
}

/**
 * Delete or Archive Order with strict Outlet Isolation and mandatory Post-Delete Database Verification
 */
export async function deleteOrder(
  orderId: string,
  requesterOutletId?: string,
  isAdminRole?: string
): Promise<DeleteOrderResult> {
  if (!orderId) {
    return {
      success: false,
      deleted: false,
      verified: false,
      error: 'ID pesanan tidak valid.',
    };
  }

  const cleanNumber = orderId.replace(/^#/, '').trim();

  // 1. Fetch the exact order to verify outlet isolation and obtain actual database ID
  let targetDbId = orderId;
  let targetOutletId = requesterOutletId;
  let existingOrder: CustomerOrder | null = null;

  try {
    existingOrder = await fetchSingleOrder(orderId);
    if (existingOrder) {
      targetDbId = existingOrder.id;
      targetOutletId = existingOrder.outletId;

      if (requesterOutletId && requesterOutletId !== 'ALL' && isAdminRole !== 'super_admin') {
        if (!matchesOutlet(existingOrder.outletId, requesterOutletId)) {
          return {
            success: false,
            deleted: false,
            verified: false,
            error: 'Akses Ditolak: Anda tidak memiliki wewenang menghapus pesanan dari cabang lain.',
          };
        }
      }
    }
  } catch (err: any) {
    console.warn('[deleteOrder fetchSingleOrder Notice]:', err?.message || err);
  }

  const client = getSupabase(isAdminRole || 'super_admin', targetOutletId);

  const idVariants = Array.from(new Set([
    targetDbId,
    orderId,
    cleanNumber,
    `#${cleanNumber}`,
    existingOrder?.orderNumber,
    existingOrder?.orderNumber?.replace(/^#/, ''),
    existingOrder?.orderNumber ? `#${existingOrder.orderNumber.replace(/^#/, '')}` : null,
  ].filter(Boolean))) as string[];

  // 2. Check if order has linked financial records in Courier Wallet (courier_earnings or courier_wallet_transactions)
  let hasFinancialHistory = false;
  try {
    const earningFilter = idVariants.flatMap(v => [`order_id.eq.${v}`, `order_number.eq.${v}`]).join(',');
    const { data: earningRows } = await client
      .from('courier_earnings')
      .select('id, status')
      .or(earningFilter);

    if (Array.isArray(earningRows) && earningRows.length > 0) {
      hasFinancialHistory = true;
    } else {
      const txFilter = idVariants.flatMap(v => [`reference_order_id.eq.${v}`, `reference_code.eq.${v}`]).join(',');
      const { data: txRows } = await client
        .from('courier_wallet_transactions')
        .select('id')
        .or(txFilter);
      if (Array.isArray(txRows) && txRows.length > 0) {
        hasFinancialHistory = true;
      }
    }
  } catch (checkErr) {
    console.warn('[deleteOrder financial history check notice]:', checkErr);
  }

  // Helper function to safely execute archiving when financial records exist
  const executeSafeArchive = async (): Promise<DeleteOrderResult> => {
    try {
      const adminToken = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
      await client.rpc('courier_reverse_order_earning', {
        p_order_id: targetDbId,
        p_reason: 'Diarsip oleh Admin (Memiliki Riwayat Keuangan Courier Wallet)',
        p_token: adminToken || null,
      });
    } catch (revErr) {
      console.warn('[deleteOrder courier_reverse_order_earning notice]:', revErr);
    }

    const nowIso = new Date().toISOString();
    try {
      await client
        .from('orders')
        .update({
          order_status: 'CANCELLED',
          rejection_reason: 'Diarsip oleh Admin (Memiliki Riwayat Keuangan Courier Wallet)',
          payment_verified_at: nowIso,
          updated_at: nowIso,
        })
        .or(`id.eq.${targetDbId},id.eq.${orderId},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`);
    } catch (archErr: any) {
      console.warn('[deleteOrder archive update notice]:', archErr?.message || archErr);
    }

    // Call backend API sync for archive status
    try {
      const adminToken = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
      await fetch(getApiUrl(`/api/orders/${encodeURIComponent(targetDbId)}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'x-admin-role': isAdminRole || 'super_admin',
          'x-outlet-id': targetOutletId || '',
        },
      });
    } catch {}

    return {
      success: true,
      deleted: false,
      isArchived: true,
      verified: true,
      message: `Pesanan #${cleanNumber} memiliki riwayat keuangan Courier Wallet dan telah diubah statusnya menjadi DIBATALKAN / DIARSIP demi menjaga integritas audit keuangan.`,
    };
  };

  // 3. If financial records exist: perform safe reversal if needed, then soft-delete/archive (order_status = CANCELLED)
  if (hasFinancialHistory) {
    return await executeSafeArchive();
  }

  // 4. Perform direct deletion in Supabase database for orders WITHOUT financial history
  let supabaseDeleteSuccess = false;
  let supabaseDeleteError: string | null = null;

  try {
    // Unlink optional non-financial references first
    try {
      await client.from('reward_redemptions').update({ reference_order_id: null }).or(`reference_order_id.eq.${targetDbId},reference_order_id.eq.${orderId}`);
      await client.from('loyalty_transactions').update({ reference_order_id: null }).or(`reference_order_id.eq.${targetDbId},reference_order_id.eq.${orderId}`);
    } catch {}

    // Delete child rows in order_items table to satisfy foreign key constraints
    try {
      await client
        .from('order_items')
        .delete()
        .or(`order_id.eq.${targetDbId},order_id.eq.${orderId},order_id.eq.${cleanNumber}`);
    } catch (childErr: any) {
      console.warn('[Supabase deleteOrder order_items warning]:', childErr?.message || childErr);
    }

    // Delete record from orders table
    const { error: delErr } = await client
      .from('orders')
      .delete()
      .or(`id.eq.${targetDbId},id.eq.${orderId},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`);

    if (delErr) {
      console.warn('[Supabase deleteOrder Notice]:', delErr.message);
      if (delErr.message?.includes('foreign key constraint') || delErr.message?.includes('courier_earnings') || delErr.message?.includes('courier_wallet_transactions')) {
        console.info('[deleteOrder] FK constraint caught during deletion. Converting to safe archive flow...');
        return await executeSafeArchive();
      }
      supabaseDeleteError = delErr.message;
    } else {
      supabaseDeleteSuccess = true;
    }
  } catch (err: any) {
    console.warn('[Supabase deleteOrder Exception]:', err);
    if (err?.message?.includes('foreign key constraint') || err?.message?.includes('courier_earnings') || err?.message?.includes('courier_wallet_transactions')) {
      console.info('[deleteOrder] FK exception caught. Converting to safe archive flow...');
      return await executeSafeArchive();
    }
    supabaseDeleteError = err?.message || 'Gagal menghapus dari Supabase';
  }

  // Perform backend server delete sync (Express backend + local JSON storage + server Supabase client)
  try {
    const adminToken = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
    const serverRes = await fetch(getApiUrl(`/api/orders/${encodeURIComponent(targetDbId)}`), {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'x-admin-role': isAdminRole || 'super_admin',
        'x-outlet-id': targetOutletId || '',
      },
    });

    if (serverRes.ok) {
      const serverJson = await serverRes.json();
      if (serverJson.verified) {
        supabaseDeleteSuccess = true;
      }
    }
  } catch (apiErr: any) {
    console.warn('[Server deleteOrder API Warning]:', apiErr?.message || apiErr);
  }

  // MANDATORY POST-DELETE DATABASE VERIFICATION (SELECT query on Supabase)
  let verifiedEmpty = false;
  try {
    const verifyClient = getSupabase(isAdminRole || 'super_admin', targetOutletId);
    const { data: remainingRows, error: verifyErr } = await verifyClient
      .from('orders')
      .select('id, order_number')
      .or(`id.eq.${targetDbId},id.eq.${orderId},order_number.eq.${orderId},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`);

    if (verifyErr) {
      console.warn('[deleteOrder Verification Query Warning]:', verifyErr.message);
    }

    if (!remainingRows || remainingRows.length === 0) {
      verifiedEmpty = true;
    } else {
      verifiedEmpty = false;
      console.error('[deleteOrder Verification Failed]: Record still found in database:', remainingRows);
    }
  } catch (vErr: any) {
    console.warn('[deleteOrder Verification Exception]:', vErr);
  }

  if (!verifiedEmpty) {
    return {
      success: false,
      deleted: false,
      verified: false,
      error: supabaseDeleteError || `Verifikasi gagal: Record pesanan (${cleanNumber}) masih ditemukan di Supabase. Periksa izin RLS DELETE pada Supabase.`,
    };
  }

  // 5. Clean up local storage cache only after verified database deletion
  try {
    // Clean up local storage cache
    const cached = safeGetItem(ADMIN_ORDERS_CACHE_KEY);
    if (cached) {
      let list: CustomerOrder[] = JSON.parse(cached);
      list = list.filter((o) => o.id !== targetDbId && o.id !== orderId && o.orderNumber !== cleanNumber && o.orderNumber !== `#${cleanNumber}`);
      safeSetItem(ADMIN_ORDERS_CACHE_KEY, JSON.stringify(stripHeavyBase64Images(list).slice(0, 50)));
    }

    // Clean up leton_content backup registry if present
    try {
      const client = getSupabase(isAdminRole || 'super_admin', targetOutletId);
      const { data: regRow } = await client
        .from('leton_content')
        .select('content')
        .eq('id', 'orders_registry')
        .maybeSingle();

      if (regRow?.content?.orders && Array.isArray(regRow.content.orders)) {
        const filteredOrders = regRow.content.orders.filter(
          (o: any) => o.id !== targetDbId && o.id !== orderId && o.orderNumber !== cleanNumber && o.orderNumber !== `#${cleanNumber}`
        );
        await client
          .from('leton_content')
          .update({ content: { ...regRow.content, orders: filteredOrders } })
          .eq('id', 'orders_registry');
      }
    } catch {}
  } catch {}

  return {
    success: true,
    deleted: true,
    verified: true,
  };
}

/**
 * Realtime Subscription for Orders
 * Listens for new and updated orders via Supabase Postgres Realtime Channel,
 * Server-Sent Events (SSE), and backup polling.
 * If targetOutletId is set, filters notifications and alert sounds specifically for that outlet.
 */
export function subscribeToOrdersRealtime(
  onOrdersChange: (updatedList: CustomerOrder[]) => void,
  onNewOrderAlert?: (newOrder: CustomerOrder) => void,
  targetOutletId?: string
): () => void {
  let isSubscribed = true;
  let clientChannel: any = null;
  let sseSource: EventSource | null = null;
  const knownOrderIds = new Set<string>();
  const notifiedConfirmedOrderIds = new Set<string>();
  let isInitialLoadDone = false;

  const activeOutlet = targetOutletId || (typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '');
  const activeRole = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') || '' : '';
  const filterOutletId = activeOutlet && activeOutlet !== 'ALL' ? activeOutlet : undefined;

  const triggerAlertIfNeeded = (order: CustomerOrder) => {
    if (!order || !order.id) return;
    // Super Admin / Central TIDAK menerima operational new-order notification popup/chime
    if (activeRole === 'super_admin') return;
    // Routing outlet check: hanya admin cabang yang sesuai
    if (filterOutletId && !matchesOutlet(order.outletId, filterOutletId)) return;
    // PENTING: Jangan kirim notifikasi/bunyi chime ke admin jika pesanan DANA belum lunas / masih pending
    if (order.paymentMethod === 'DANA' && order.paymentStatus !== 'PAID') {
      return;
    }
    if (onNewOrderAlert) {
      onNewOrderAlert(order);
    }
  };

  // Function to refresh and notify
  const refresh = async (alertOrder?: CustomerOrder) => {
    if (!isSubscribed) return;
    const orders = await fetchAllOrders(filterOutletId);
    if (!isSubscribed) return;
    onOrdersChange(orders);

    if (!isInitialLoadDone) {
      orders.forEach((o) => {
        knownOrderIds.add(o.id);
        if (o.paymentMethod === 'DANA' && o.paymentStatus === 'PAID') {
          notifiedConfirmedOrderIds.add(o.id);
        }
      });
      isInitialLoadDone = true;
    } else {
      // Find any newly discovered order from cloud fetch
      const newlyDiscovered = orders.filter((o) => !knownOrderIds.has(o.id));
      newlyDiscovered.forEach((o) => {
        knownOrderIds.add(o.id);
        // Hanya notifikasi pesanan non-DANA atau pesanan DANA yang sudah PAID
        if (o.paymentMethod !== 'DANA' || o.paymentStatus === 'PAID') {
          if (o.paymentMethod === 'DANA' && o.paymentStatus === 'PAID') {
            notifiedConfirmedOrderIds.add(o.id);
          }
          triggerAlertIfNeeded(o);
        }
      });

      // Deteksi jika pesanan DANA yang sebelumnya pending kini telah diverifikasi PAID
      orders.forEach((o) => {
        if (
          o.paymentMethod === 'DANA' &&
          o.paymentStatus === 'PAID' &&
          !notifiedConfirmedOrderIds.has(o.id)
        ) {
          notifiedConfirmedOrderIds.add(o.id);
          triggerAlertIfNeeded(o);
        }
      });
    }

    if (alertOrder && alertOrder.id) {
      knownOrderIds.add(alertOrder.id);
      if (alertOrder.paymentMethod === 'DANA' && alertOrder.paymentStatus === 'PAID') {
        notifiedConfirmedOrderIds.add(alertOrder.id);
      }
      triggerAlertIfNeeded(alertOrder);
    }
  };

  // Initial load
  refresh();

  // Debounce helper (300ms window) to coalesce rapid realtime bursts
  // (e.g. Broadcast ORDER_CREATED + orders INSERT + orders_registry UPDATE) into a single fetchAllOrders() call
  let refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingAlertOrder: CustomerOrder | undefined = undefined;

  const clearRefreshDebounce = () => {
    if (refreshDebounceTimer !== null) {
      clearTimeout(refreshDebounceTimer);
      refreshDebounceTimer = null;
    }
    pendingAlertOrder = undefined;
  };

  const scheduleRefresh = (alertOrder?: CustomerOrder) => {
    if (!isSubscribed) return;
    if (alertOrder && alertOrder.id) {
      pendingAlertOrder = alertOrder;
    }
    if (refreshDebounceTimer !== null) {
      clearTimeout(refreshDebounceTimer);
    }
    refreshDebounceTimer = setTimeout(() => {
      refreshDebounceTimer = null;
      const orderToAlert = pendingAlertOrder;
      pendingAlertOrder = undefined;
      if (isSubscribed) {
        refresh(orderToAlert);
      }
    }, 300);
  };

  // 1. Fail-Safe Heartbeat Polling Control (Runs ONLY as a fallback when Realtime is disconnected)
  let pollInterval: ReturnType<typeof setInterval> | null = null;
  const startFallbackPolling = (intervalMs = 15000) => {
    if (!isSubscribed) return;
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    pollInterval = setInterval(() => {
      if (isSubscribed) {
        refresh();
      }
    }, intervalMs);
  };
  const stopFallbackPolling = () => {
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  // 2. Supabase Postgres Realtime & Broadcast Stream
  try {
    const client = getSupabase(activeRole, filterOutletId);
    const channelName = filterOutletId ? `leton_orders_stream_${filterOutletId}` : 'leton_orders_stream_global';
    clientChannel = client
      .channel(channelName)
      .on('broadcast', { event: 'ORDER_CREATED' }, (payload: any) => {
        const newOrder = payload?.payload;
        if (newOrder && newOrder.id) {
          // Jangan alert jika pesanan DANA belum lunas
          if (newOrder.paymentMethod === 'DANA' && newOrder.paymentStatus !== 'PAID') {
            scheduleRefresh();
          } else {
            scheduleRefresh(newOrder);
          }
        } else {
          scheduleRefresh();
        }
      })
      .on('broadcast', { event: 'ORDER_DANA_CONFIRMED' }, (payload: any) => {
        const confirmedOrder = payload?.payload;
        if (confirmedOrder && confirmedOrder.id) {
          if (!notifiedConfirmedOrderIds.has(confirmedOrder.id)) {
            notifiedConfirmedOrderIds.add(confirmedOrder.id);
            scheduleRefresh(confirmedOrder);
          } else {
            scheduleRefresh();
          }
        } else {
          scheduleRefresh();
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
        },
        (payload: any) => {
          if (payload.eventType === 'DELETE') {
            if (payload.old && payload.old.id) {
              knownOrderIds.delete(payload.old.id);
            }
            scheduleRefresh();
          } else if (payload.eventType === 'INSERT' && payload.new) {
            const raw = payload.new;
            const newOrder: CustomerOrder = {
              id: raw.id,
              orderNumber: raw.order_number || raw.orderNumber || 'LTN-????',
              outletId: raw.outlet_id || raw.outletId || '',
              outletName: raw.outlet_name || raw.outletName || '',
              customerName: raw.customer_name || raw.customerName || '',
              customerPhone: raw.customer_phone || raw.customerPhone || '',
              orderType: raw.order_type || raw.orderType || 'DINE IN',
              tableNumber: raw.table_number || raw.tableNumber || '',
              items: Array.isArray(raw.items) ? raw.items : [],
              totalAmount: Number(raw.total_amount || raw.totalAmount || 0),
              paymentMethod: raw.payment_method || raw.paymentMethod || 'QRIS',
              paymentStatus: raw.payment_status || raw.paymentStatus || 'WAITING PAYMENT',
              paymentProofPath: raw.payment_proof_path || raw.payment_receipt_path || raw.paymentReceiptPath,
              paymentReceiptUrl: raw.payment_receipt_url || raw.paymentReceiptUrl,
              paymentReceiptPath: raw.payment_receipt_path || raw.payment_proof_path || raw.paymentReceiptPath,
              rejectionReason: raw.rejection_reason || raw.rejectionReason,
              orderStatus: raw.order_status || raw.orderStatus || 'NEW',
              customerNote: raw.customer_note || raw.customerNote || '',
              voucherId: raw.voucher_id || raw.voucherId || undefined,
              voucherCode: raw.voucher_code || raw.voucherCode || undefined,
              discountAmount: raw.discount_amount !== undefined && raw.discount_amount !== null ? Number(raw.discount_amount) : (raw.discountAmount !== undefined ? Number(raw.discountAmount) : 0),
              pickupTime: raw.pickup_time || raw.pickupTime || undefined,
              pickup_time: raw.pickup_time || raw.pickupTime || undefined,
              deliveryAddress: raw.delivery_address || raw.deliveryAddress || undefined,
              delivery_address: raw.delivery_address || raw.deliveryAddress || undefined,
              customerLat: raw.customer_lat !== undefined && raw.customer_lat !== null ? Number(raw.customer_lat) : (raw.customerLat !== undefined && raw.customerLat !== null ? Number(raw.customerLat) : undefined),
              customer_lat: raw.customer_lat !== undefined && raw.customer_lat !== null ? Number(raw.customer_lat) : (raw.customerLat !== undefined && raw.customerLat !== null ? Number(raw.customerLat) : undefined),
              customerLng: raw.customer_lng !== undefined && raw.customer_lng !== null ? Number(raw.customer_lng) : (raw.customerLng !== undefined && raw.customerLng !== null ? Number(raw.customerLng) : undefined),
              customer_lng: raw.customer_lng !== undefined && raw.customer_lng !== null ? Number(raw.customer_lng) : (raw.customerLng !== undefined && raw.customerLng !== null ? Number(raw.customerLng) : undefined),
              deliveryDistance: raw.delivery_distance !== undefined && raw.delivery_distance !== null ? Number(raw.delivery_distance) : (raw.deliveryDistance !== undefined && raw.deliveryDistance !== null ? Number(raw.deliveryDistance) : undefined),
              delivery_distance: raw.delivery_distance !== undefined && raw.delivery_distance !== null ? Number(raw.delivery_distance) : (raw.deliveryDistance !== undefined && raw.deliveryDistance !== null ? Number(raw.deliveryDistance) : undefined),
              deliveryFee: raw.delivery_fee !== undefined && raw.delivery_fee !== null ? Number(raw.delivery_fee) : (raw.deliveryFee !== undefined && raw.deliveryFee !== null ? Number(raw.deliveryFee) : undefined),
              delivery_fee: raw.delivery_fee !== undefined && raw.delivery_fee !== null ? Number(raw.delivery_fee) : (raw.deliveryFee !== undefined && raw.deliveryFee !== null ? Number(raw.deliveryFee) : undefined),
              serviceFee: resolvePersistedOrderServiceFee(raw),
              service_fee: resolvePersistedOrderServiceFee(raw),
              deliveryStatus: raw.delivery_status || raw.deliveryStatus || undefined,
              delivery_status: raw.delivery_status || raw.deliveryStatus || undefined,
              courierUsername: raw.courier_username || raw.courierUsername || undefined,
              courier_username: raw.courier_username || raw.courierUsername || undefined,
              createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
            };
            // Jangan jadwalkan notifikasi suara/banner jika pesanan DANA masih menunggu pembayaran
            if (newOrder.paymentMethod === 'DANA' && newOrder.paymentStatus !== 'PAID') {
              scheduleRefresh();
            } else {
              scheduleRefresh(newOrder);
            }
          } else if (payload.eventType === 'UPDATE' && payload.new) {
            const raw = payload.new;
            const updatedOrder: CustomerOrder = {
              id: raw.id,
              orderNumber: raw.order_number || raw.orderNumber || 'LTN-????',
              outletId: raw.outlet_id || raw.outletId || '',
              outletName: raw.outlet_name || raw.outletName || '',
              customerName: raw.customer_name || raw.customerName || '',
              customerPhone: raw.customer_phone || raw.customerPhone || '',
              orderType: raw.order_type || raw.orderType || 'DINE IN',
              tableNumber: raw.table_number || raw.tableNumber || '',
              items: Array.isArray(raw.items) ? raw.items : [],
              totalAmount: Number(raw.total_amount || raw.totalAmount || 0),
              paymentMethod: raw.payment_method || raw.paymentMethod || 'QRIS',
              paymentStatus: raw.payment_status || raw.paymentStatus || 'WAITING PAYMENT',
              paymentProofPath: raw.payment_proof_path || raw.payment_receipt_path || raw.paymentReceiptPath,
              paymentReceiptUrl: raw.payment_receipt_url || raw.paymentReceiptUrl,
              paymentReceiptPath: raw.payment_receipt_path || raw.payment_proof_path || raw.paymentReceiptPath,
              rejectionReason: raw.rejection_reason || raw.rejectionReason,
              orderStatus: raw.order_status || raw.orderStatus || 'NEW',
              customerNote: raw.customer_note || raw.customerNote || '',
              voucherId: raw.voucher_id || raw.voucherId || undefined,
              voucherCode: raw.voucher_code || raw.voucherCode || undefined,
              discountAmount: raw.discount_amount !== undefined && raw.discount_amount !== null ? Number(raw.discount_amount) : (raw.discountAmount !== undefined ? Number(raw.discountAmount) : 0),
              pickupTime: raw.pickup_time || raw.pickupTime || undefined,
              pickup_time: raw.pickup_time || raw.pickupTime || undefined,
              deliveryAddress: raw.delivery_address || raw.deliveryAddress || undefined,
              delivery_address: raw.delivery_address || raw.deliveryAddress || undefined,
              customerLat: raw.customer_lat !== undefined && raw.customer_lat !== null ? Number(raw.customer_lat) : (raw.customerLat !== undefined && raw.customerLat !== null ? Number(raw.customerLat) : undefined),
              customer_lat: raw.customer_lat !== undefined && raw.customer_lat !== null ? Number(raw.customer_lat) : (raw.customerLat !== undefined && raw.customerLat !== null ? Number(raw.customerLat) : undefined),
              customerLng: raw.customer_lng !== undefined && raw.customer_lng !== null ? Number(raw.customer_lng) : (raw.customerLng !== undefined && raw.customerLng !== null ? Number(raw.customerLng) : undefined),
              customer_lng: raw.customer_lng !== undefined && raw.customer_lng !== null ? Number(raw.customer_lng) : (raw.customerLng !== undefined && raw.customerLng !== null ? Number(raw.customerLng) : undefined),
              deliveryDistance: raw.delivery_distance !== undefined && raw.delivery_distance !== null ? Number(raw.delivery_distance) : (raw.deliveryDistance !== undefined && raw.deliveryDistance !== null ? Number(raw.deliveryDistance) : undefined),
              delivery_distance: raw.delivery_distance !== undefined && raw.delivery_distance !== null ? Number(raw.delivery_distance) : (raw.deliveryDistance !== undefined && raw.deliveryDistance !== null ? Number(raw.deliveryDistance) : undefined),
              deliveryFee: raw.delivery_fee !== undefined && raw.delivery_fee !== null ? Number(raw.delivery_fee) : (raw.deliveryFee !== undefined && raw.deliveryFee !== null ? Number(raw.deliveryFee) : undefined),
              delivery_fee: raw.delivery_fee !== undefined && raw.delivery_fee !== null ? Number(raw.delivery_fee) : (raw.deliveryFee !== undefined && raw.deliveryFee !== null ? Number(raw.deliveryFee) : undefined),
              serviceFee: resolvePersistedOrderServiceFee(raw),
              service_fee: resolvePersistedOrderServiceFee(raw),
              deliveryStatus: raw.delivery_status || raw.deliveryStatus || undefined,
              delivery_status: raw.delivery_status || raw.deliveryStatus || undefined,
              courierUsername: raw.courier_username || raw.courierUsername || undefined,
              courier_username: raw.courier_username || raw.courierUsername || undefined,
              createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
            };
            // Jika pesanan DANA baru saja diverifikasi dan berstatus PAID, kirim notifikasi admin tepat 1 kali
            if (
              updatedOrder.paymentMethod === 'DANA' &&
              updatedOrder.paymentStatus === 'PAID' &&
              !notifiedConfirmedOrderIds.has(updatedOrder.id)
            ) {
              notifiedConfirmedOrderIds.add(updatedOrder.id);
              scheduleRefresh(updatedOrder);
            } else {
              scheduleRefresh();
            }
          } else {
            scheduleRefresh();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leton_content',
          filter: 'id=eq.orders_registry',
        },
        () => {
          scheduleRefresh();
        }
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          stopFallbackPolling();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // Fallback polling only when Realtime experiences connection issues
          startFallbackPolling(15000);
        }
      });
  } catch (err) {
    console.warn('[Orders Realtime Subscription Error]:', err);
    startFallbackPolling(15000);
  }

  // Return cleanup
  return () => {
    isSubscribed = false;
    clearRefreshDebounce();
    stopFallbackPolling();
    if (clientChannel) {
      try {
        const client = getSupabase(activeRole, filterOutletId);
        client.removeChannel(clientChannel);
      } catch {
        // ignore
      }
    }
  };
}

/**
 * Fetch a single order by ID or Order Number
 * Uses SECURITY DEFINER RPC get_guest_order_status for guest public tracking,
 * or direct table access if an admin role is active.
 */
export async function fetchSingleOrder(
  orderIdOrNumber: string,
  customerPhone?: string
): Promise<CustomerOrder | null> {
  if (!orderIdOrNumber) return null;
  const cleanNumber = orderIdOrNumber.replace(/^#/, '').trim();

  // 1. Admin / Courier Flow (If admin or courier role is active in localStorage)
  const activeRole = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_role') : '';
  if (activeRole === 'super_admin' || activeRole === 'outlet_admin' || activeRole === 'courier') {
    try {
      const client = getSupabase(activeRole);
      const { data, error } = await client
        .from('orders')
        .select('*')
        .or(`id.eq.${orderIdOrNumber},id.eq.${cleanNumber},order_number.eq.${orderIdOrNumber},order_number.eq.${cleanNumber},order_number.eq.#${cleanNumber}`)
        .maybeSingle();

      if (!error && data) {
        const singleOrd: CustomerOrder = {
          id: data.id,
          orderNumber: data.order_number || data.orderNumber || 'LTN-????',
          outletId: data.outlet_id || data.outletId || '',
          outletName: data.outlet_name || data.outletName || '',
          customerName: data.customer_name || data.customerName || '',
          customerPhone: data.customer_phone || data.customerPhone || '',
          customerId: data.customer_id || data.customerId || undefined,
          userId: data.user_id || data.userId || undefined,
          orderType: data.order_type || data.orderType || 'DINE IN',
          tableNumber: data.table_number || data.tableNumber || '',
          items: Array.isArray(data.items) ? data.items : [],
          totalAmount: Number(data.total_amount || data.totalAmount || 0),
          paymentMethod: data.payment_method || data.paymentMethod || 'QRIS',
          paymentStatus: data.payment_status || data.paymentStatus || 'WAITING PAYMENT',
          paymentProofPath: data.payment_proof_path || data.payment_receipt_path || data.paymentReceiptPath,
          paymentReceiptUrl: data.payment_receipt_url || data.paymentReceiptUrl,
          paymentReceiptPath: data.payment_receipt_path || data.payment_proof_path || data.paymentReceiptPath,
          rejectionReason: data.rejection_reason || data.rejectionReason,
          orderStatus: data.order_status || data.orderStatus || 'NEW',
          customerNote: data.customer_note || data.customerNote || '',
          voucherId: data.voucher_id || data.voucherId || undefined,
          voucherCode: data.voucher_code || data.voucherCode || undefined,
          discountAmount: data.discount_amount !== undefined && data.discount_amount !== null ? Number(data.discount_amount) : (data.discountAmount !== undefined ? Number(data.discountAmount) : 0),
          discount_amount: data.discount_amount !== undefined && data.discount_amount !== null ? Number(data.discount_amount) : (data.discountAmount !== undefined ? Number(data.discountAmount) : 0),
          deliveryAddress: data.delivery_address || data.deliveryAddress || undefined,
          delivery_address: data.delivery_address || data.deliveryAddress || undefined,
          customerLat: data.customer_lat !== undefined && data.customer_lat !== null ? Number(data.customer_lat) : (data.customerLat !== undefined && data.customerLat !== null ? Number(data.customerLat) : undefined),
          customer_lat: data.customer_lat !== undefined && data.customer_lat !== null ? Number(data.customer_lat) : (data.customerLat !== undefined && data.customerLat !== null ? Number(data.customerLat) : undefined),
          customerLng: data.customer_lng !== undefined && data.customer_lng !== null ? Number(data.customer_lng) : (data.customerLng !== undefined && data.customerLng !== null ? Number(data.customerLng) : undefined),
          customer_lng: data.customer_lng !== undefined && data.customer_lng !== null ? Number(data.customer_lng) : (data.customerLng !== undefined && data.customerLng !== null ? Number(data.customerLng) : undefined),
          deliveryDistance: data.delivery_distance !== undefined && data.delivery_distance !== null ? Number(data.delivery_distance) : (data.deliveryDistance !== undefined && data.deliveryDistance !== null ? Number(data.deliveryDistance) : undefined),
          delivery_distance: data.delivery_distance !== undefined && data.delivery_distance !== null ? Number(data.delivery_distance) : (data.deliveryDistance !== undefined && data.deliveryDistance !== null ? Number(data.deliveryDistance) : undefined),
          deliveryFee: data.delivery_fee !== undefined && data.delivery_fee !== null ? Number(data.delivery_fee) : (data.deliveryFee !== undefined && data.deliveryFee !== null ? Number(data.deliveryFee) : undefined),
          delivery_fee: data.delivery_fee !== undefined && data.delivery_fee !== null ? Number(data.delivery_fee) : (data.deliveryFee !== undefined && data.deliveryFee !== null ? Number(data.deliveryFee) : undefined),
          serviceFee: resolvePersistedOrderServiceFee(data),
          service_fee: resolvePersistedOrderServiceFee(data),
          deliveryStatus: data.delivery_status || data.deliveryStatus || undefined,
          delivery_status: data.delivery_status || data.deliveryStatus || undefined,
          courierUsername: data.courier_username || data.courierUsername || undefined,
          courier_username: data.courier_username || data.courierUsername || undefined,
          createdAt: data.created_at || data.createdAt || new Date().toISOString(),
          updatedAt: data.updated_at || data.updatedAt,
        };
        const [enrichedSingle] = await enrichOrdersFromRegistry([singleOrd]);
        return enrichedSingle;
      }
    } catch (err) {
      console.warn('[Fetch Single Admin Order Note]:', err);
    }
  }

  // Resolve fallback customerPhone from local/session storage ONLY if order matches or belongs to current session
  let resolvedPhone = (customerPhone || '').trim();
  if (!resolvedPhone && typeof window !== 'undefined') {
    try {
      const rawDana =
        sessionStorage.getItem('leton_dana_pending_order') ||
        localStorage.getItem('leton_dana_pending_order');
      if (rawDana) {
        const parsedDana = JSON.parse(rawDana);
        if (
          parsedDana?.customerPhone &&
          (parsedDana.id === orderIdOrNumber ||
            parsedDana.orderNumber === orderIdOrNumber ||
            String(parsedDana.orderNumber || '').replace(/^#/, '') === cleanNumber)
        ) {
          resolvedPhone = String(parsedDana.customerPhone).trim();
        }
      }
      if (!resolvedPhone) {
        const rawGuest = localStorage.getItem('leton_guest_checkout_info');
        if (rawGuest) {
          const parsedGuest = JSON.parse(rawGuest);
          if (parsedGuest?.phone) {
            resolvedPhone = String(parsedGuest.phone).trim();
          }
        }
      }
      if (!resolvedPhone) {
        const rawProfile = localStorage.getItem('leton_customer_profile');
        if (rawProfile) {
          const parsedProfile = JSON.parse(rawProfile);
          if (parsedProfile?.nomorHp || parsedProfile?.phone) {
            resolvedPhone = String(parsedProfile.nomorHp || parsedProfile.phone).trim();
          }
        }
      }
    } catch {}
  }

  // 2. Public / Guest Flow via Security Definer RPC (2-Factor Authorization: Order Identifier + Phone)
  const cleanPhoneDigits = resolvedPhone.replace(/[^0-9]/g, '');
  if (cleanPhoneDigits.length >= 8) {
    try {
      const client = getSupabase();
      const { data: rpcRes, error: rpcErr } = await client.rpc('get_guest_order_status', {
        p_order_identifier: orderIdOrNumber,
        p_phone: resolvedPhone,
      });

      if (!rpcErr && rpcRes && rpcRes.success && rpcRes.order) {
        const o = rpcRes.order;
        const guestOrd: CustomerOrder = {
          id: o.id,
          orderNumber: o.orderNumber || 'LTN-????',
          outletId: o.outletId || '',
          outletName: o.outletName || '',
          customerName: o.customerName || '',
          customerPhone: resolvedPhone || o.customerPhone || '',
          orderType: o.orderType || 'DINE IN',
          items: Array.isArray(o.items) ? o.items : [],
          totalAmount: Number(o.totalAmount || 0),
          paymentMethod: o.paymentMethod || 'QRIS',
          paymentStatus: o.paymentStatus || 'WAITING PAYMENT',
          orderStatus: o.orderStatus || 'NEW',
          customerNote: o.customerNote || '',
          deliveryAddress: o.deliveryAddress || o.delivery_address || undefined,
          delivery_address: o.deliveryAddress || o.delivery_address || undefined,
          customerLat: o.customerLat ?? o.customer_lat ?? undefined,
          customer_lat: o.customerLat ?? o.customer_lat ?? undefined,
          customerLng: o.customerLng ?? o.customer_lng ?? undefined,
          customer_lng: o.customerLng ?? o.customer_lng ?? undefined,
          deliveryDistance: o.deliveryDistance ?? o.delivery_distance ?? undefined,
          delivery_distance: o.deliveryDistance ?? o.delivery_distance ?? undefined,
          deliveryFee: o.deliveryFee ?? o.delivery_fee ?? undefined,
          delivery_fee: o.deliveryFee ?? o.delivery_fee ?? undefined,
          serviceFee: resolvePersistedOrderServiceFee(o),
          service_fee: resolvePersistedOrderServiceFee(o),
          deliveryStatus: o.deliveryStatus || o.delivery_status || undefined,
          delivery_status: o.deliveryStatus || o.delivery_status || undefined,
          createdAt: o.createdAt || new Date().toISOString(),
          updatedAt: o.updatedAt,
        };
        const [enrichedGuest] = await enrichOrdersFromRegistry([guestOrd]);
        return enrichedGuest;
      }
    } catch (err) {
      console.warn('[Fetch Single Guest Order RPC Note]:', err);
    }
  }

  return null;
}

/**
 * Realtime Subscription for a Single Customer Order
 * Ensures instant UI updates on the customer's Order Confirmation / Bill screen
 */
export function subscribeToSingleOrder(
  orderIdOrNumber: string,
  customerPhone: string | undefined,
  onUpdate: (order: CustomerOrder) => void
): () => void {
  let isSubscribed = true;
  let isRealtimeSubscribed = false;
  let clientChannel: any = null;
  let sseSource: EventSource | null = null;
  let latestPaymentStatus = 'WAITING PAYMENT';
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const stopFallbackPolling = () => {
    if (pollInterval !== null) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const isTerminalState = (order: CustomerOrder): boolean => {
    const paySt = String(order.paymentStatus || '').toUpperCase();
    const ordSt = String(order.orderStatus || '').toUpperCase();
    return (
      paySt === 'PAYMENT REJECTED' ||
      ordSt === 'COMPLETED' ||
      ordSt === 'CANCELLED' ||
      ordSt === 'REJECTED'
    );
  };

  const checkOrder = async () => {
    if (!isSubscribed) return;
    const latest = await fetchSingleOrder(orderIdOrNumber, customerPhone);
    if (latest && isSubscribed) {
      latestPaymentStatus = latest.paymentStatus || latestPaymentStatus;
      onUpdate(latest);
      if (isTerminalState(latest) || (isRealtimeSubscribed && latestPaymentStatus !== 'WAITING PAYMENT')) {
        stopFallbackPolling();
      }
    }
  };

  // 1. Initial fetch
  checkOrder();

  // 2. Fallback Polling Control (Also keeps polling while paymentStatus is WAITING PAYMENT)
  const startFallbackPolling = () => {
    if (!isSubscribed || pollInterval !== null) return;
    pollInterval = setInterval(() => {
      if (isSubscribed) {
        checkOrder();
      }
    }, 3000);
  };

  startFallbackPolling();

  // 3. Supabase Postgres Realtime Channel
  try {
    const client = getSupabase();
    const cleanRef = orderIdOrNumber.replace(/^#/, '').trim();
    clientChannel = client
      .channel(`order_live_${orderIdOrNumber}_${Math.random().toString(36).slice(2)}`)
      .on('broadcast', { event: 'ORDER_STATUS_UPDATED' }, (payload: any) => {
        if (
          payload?.payload?.order_id === orderIdOrNumber ||
          payload?.payload?.order_number === orderIdOrNumber ||
          payload?.payload?.order_number === cleanRef
        ) {
          checkOrder();
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
        },
        (payload: any) => {
          if (
            payload.new &&
            (payload.new.id === orderIdOrNumber ||
              payload.new.order_number === orderIdOrNumber ||
              payload.new.order_number === cleanRef)
          ) {
            checkOrder();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leton_content',
          filter: 'id=eq.orders_registry',
        },
        () => {
          checkOrder();
        }
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          isRealtimeSubscribed = true;
          if (latestPaymentStatus !== 'WAITING PAYMENT') {
            stopFallbackPolling();
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          isRealtimeSubscribed = false;
          startFallbackPolling();
        }
      });
  } catch (err) {
    console.warn('[Single Order Realtime Error]:', err);
    startFallbackPolling();
  }

  // 4. SSE Listener
  try {
    if (typeof window !== 'undefined' && window.EventSource) {
      sseSource = new EventSource(getApiUrl('/api/events'));
      sseSource.onerror = (e) => {
        try {
          if (e && typeof (e as any).preventDefault === 'function') {
            (e as any).preventDefault();
          }
        } catch {}
        try { sseSource?.close(); } catch {}
      };
      sseSource.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (
            payload &&
            (payload.type === 'ORDER_UPDATED' ||
              payload.type === 'ORDER_STATUS_UPDATED' ||
              payload.type === 'ORDER_CREATED')
          ) {
            if (
              payload.order?.id === orderIdOrNumber ||
              payload.order?.orderNumber === orderIdOrNumber
            ) {
              checkOrder();
            }
          }
        } catch {
          // ignore
        }
      };
    }
  } catch {
    // ignore
  }

  return () => {
    isSubscribed = false;
    stopFallbackPolling();
    if (sseSource) sseSource.close();
    if (clientChannel) {
      try {
        const client = getSupabase();
        client.removeChannel(clientChannel);
      } catch {
        // ignore
      }
    }
  };
}

/**
 * Fetch all orders for a specific customer (for Admin Pusat -> Data Customer detail view)
 */
export async function fetchCustomerOrdersForAdmin(
  customerId: string,
  customerPhone?: string,
  customerName?: string
): Promise<CustomerOrder[]> {
  try {
    const client = getSupabase('super_admin');
    const { data, error } = await client
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error && Array.isArray(data)) {
      const cleanPhone = (customerPhone || '').replace(/[^0-9]/g, '');
      const cleanName = (customerName || '').toLowerCase().trim();

      const matchedRows = data.filter((row: any) => {
        const rowCustId = String(row.customer_id || row.customerId || '');
        const rowPhone = String(row.customer_phone || row.customerPhone || '').replace(/[^0-9]/g, '');
        const rowName = String(row.customer_name || row.customerName || '').toLowerCase().trim();

        if (customerId && rowCustId === customerId) return true;
        if (cleanPhone && cleanPhone.length >= 8 && rowPhone.includes(cleanPhone)) return true;
        if (cleanName && cleanName.length > 0 && rowName === cleanName) return true;
        return false;
      });

      // Batch verify order_items from public.order_items table
      const orderIds = matchedRows.map((r: any) => r.id).filter(Boolean);
      const itemsMap: Record<string, any[]> = {};
      if (orderIds.length > 0) {
        try {
          const { data: dbItems, error: itemsErr } = await client
            .from('order_items')
            .select('id, order_id, product_id, name, price, quantity')
            .in('order_id', orderIds);

          if (!itemsErr && Array.isArray(dbItems)) {
            dbItems.forEach((it: any) => {
              if (it && it.order_id) {
                if (!itemsMap[it.order_id]) itemsMap[it.order_id] = [];
                itemsMap[it.order_id].push(it);
              }
            });
          }
        } catch (itemErr) {
          console.warn('[fetchCustomerOrdersForAdmin] order_items fetch note:', itemErr);
        }
      }

      const mappedCustomerOrders: CustomerOrder[] = matchedRows.map((row: any) => {
        const attachedItems = itemsMap[row.id];
        const jsonItems = Array.isArray(row.items) ? row.items : [];

        return {
          id: row.id,
          orderNumber: row.order_number || row.orderNumber || 'LTN-????',
          outletId: row.outlet_id || row.outletId || '',
          outletName: row.outlet_name || row.outletName || '',
          customerName: row.customer_name || row.customerName || '',
          customerPhone: row.customer_phone || row.customerPhone || '',
          customerId: row.customer_id || row.customerId || undefined,
          orderType: row.order_type || row.orderType || 'DINE IN',
          tableNumber: row.table_number || row.tableNumber || '',
          items: jsonItems,
          order_items: attachedItems !== undefined ? attachedItems : jsonItems,
          totalAmount: Number(row.total_amount || row.totalAmount || 0),
          paymentMethod: row.payment_method || row.paymentMethod || 'QRIS',
          paymentStatus: row.payment_status || row.paymentStatus || 'WAITING PAYMENT',
          paymentReceiptUrl: row.payment_receipt_url || row.paymentReceiptUrl,
          paymentReceiptPath: row.payment_receipt_path || row.paymentReceiptPath,
          rejectionReason: row.rejection_reason || row.rejectionReason,
          orderStatus: row.order_status || row.orderStatus || 'NEW',
          customerNote: row.customer_note || row.customerNote || '',
          voucherId: row.voucher_id || row.voucherId || undefined,
          voucherCode: row.voucher_code || row.voucherCode || undefined,
          discountAmount: row.discount_amount !== undefined && row.discount_amount !== null ? Number(row.discount_amount) : (row.discountAmount !== undefined ? Number(row.discountAmount) : 0),
          discount_amount: row.discount_amount !== undefined && row.discount_amount !== null ? Number(row.discount_amount) : (row.discountAmount !== undefined ? Number(row.discountAmount) : 0),
          deliveryAddress: row.delivery_address || row.deliveryAddress || undefined,
          delivery_address: row.delivery_address || row.deliveryAddress || undefined,
          customerLat: row.customer_lat !== undefined && row.customer_lat !== null ? Number(row.customer_lat) : (row.customerLat !== undefined && row.customerLat !== null ? Number(row.customerLat) : undefined),
          customer_lat: row.customer_lat !== undefined && row.customer_lat !== null ? Number(row.customer_lat) : (row.customerLat !== undefined && row.customerLat !== null ? Number(row.customerLat) : undefined),
          customerLng: row.customer_lng !== undefined && row.customer_lng !== null ? Number(row.customer_lng) : (row.customerLng !== undefined && row.customerLng !== null ? Number(row.customerLng) : undefined),
          customer_lng: row.customer_lng !== undefined && row.customer_lng !== null ? Number(row.customer_lng) : (row.customerLng !== undefined && row.customerLng !== null ? Number(row.customerLng) : undefined),
          deliveryDistance: row.delivery_distance !== undefined && row.delivery_distance !== null ? Number(row.delivery_distance) : (row.deliveryDistance !== undefined && row.deliveryDistance !== null ? Number(row.deliveryDistance) : undefined),
          delivery_distance: row.delivery_distance !== undefined && row.delivery_distance !== null ? Number(row.delivery_distance) : (row.deliveryDistance !== undefined && row.deliveryDistance !== null ? Number(row.deliveryDistance) : undefined),
          deliveryFee: row.delivery_fee !== undefined && row.delivery_fee !== null ? Number(row.delivery_fee) : (row.deliveryFee !== undefined && row.deliveryFee !== null ? Number(row.deliveryFee) : undefined),
          delivery_fee: row.delivery_fee !== undefined && row.delivery_fee !== null ? Number(row.delivery_fee) : (row.deliveryFee !== undefined && row.deliveryFee !== null ? Number(row.deliveryFee) : undefined),
          serviceFee: resolvePersistedOrderServiceFee(row),
          service_fee: resolvePersistedOrderServiceFee(row),
          deliveryStatus: row.delivery_status || row.deliveryStatus || undefined,
          delivery_status: row.delivery_status || row.deliveryStatus || undefined,
          courierUsername: row.courier_username || row.courierUsername || undefined,
          courier_username: row.courier_username || row.courierUsername || undefined,
          pickupTime: row.pickup_time || row.pickupTime || undefined,
          pickup_time: row.pickup_time || row.pickupTime || undefined,
          createdAt: row.created_at || row.createdAt || new Date().toISOString(),
        };
      });
      return await enrichOrdersFromRegistry(mappedCustomerOrders);
    }
  } catch (err) {
    console.warn('[fetchCustomerOrdersForAdmin exception]:', err);
  }
  return [];
}

/**
 * Fetch Delivery orders for the authenticated Courier's assigned outlet.
 * Uses server-side verified session via GET /api/courier/orders with strict outlet isolation,
 * and falls back to fetchAllOrders filtered by DELIVERY and outletId if offline/unreachable.
 */
export async function fetchCourierDeliveryOrders(outletId?: string): Promise<CustomerOrder[]> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
  const storedOutlet = outletId || (typeof window !== 'undefined' ? localStorage.getItem('leton_admin_outlet') || '' : '');

  if (token) {
    try {
      const res = await fetch(getApiUrl('/api/courier/orders'), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
      });
      if (res.ok) {
        const json = await res.json();
        if (json && json.success && Array.isArray(json.orders)) {
          return await enrichOrdersFromRegistry(json.orders);
        }
      }
    } catch (err) {
      console.warn('[fetchCourierDeliveryOrders API notice]:', err);
    }
  }

  const allOrders = await fetchAllOrders(storedOutlet || undefined);
  return allOrders.filter(
    (o) =>
      String(o.orderType || '').toUpperCase().trim() === 'DELIVERY' &&
      (!storedOutlet || matchesOutlet(o.outletId, storedOutlet))
  );
}

export type CourierActionType = 'CLAIM' | 'PICK_UP' | 'START_DELIVERY' | 'COMPLETE_DELIVERY';

/**
 * Perform Courier Delivery Action ('CLAIM' | 'PICK_UP' | 'START_DELIVERY' | 'COMPLETE_DELIVERY')
 * Authenticates via server session token, enforces strict outlet isolation,
 * and executes RPC courier_claim_delivery_order when available with safe fallback.
 */
export async function performCourierOrderAction(
  orderId: string,
  action: CourierActionType
): Promise<{ success: boolean; order?: CustomerOrder; error?: string }> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('leton_admin_token') || '' : '';
  if (!token) {
    return {
      success: false,
      error: 'Sesi kurir tidak ditemukan. Silakan login kembali.',
    };
  }

  try {
    const res = await fetch(getApiUrl(`/api/courier/orders/${encodeURIComponent(orderId)}/action`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ action }),
    });

    const json = await res.json().catch(() => ({}));
    if (res.ok && json && json.success) {
      const enrichedOrder = json.order ? (await enrichOrdersFromRegistry([json.order]))[0] : undefined;
      return {
        success: true,
        order: enrichedOrder || json.order,
      };
    }

    return {
      success: false,
      error: json?.error || 'Gagal memproses aksi pengantaran pesanan.',
    };
  } catch (err: any) {
    console.error('[performCourierOrderAction network error]:', err);
    return {
      success: false,
      error: 'Gagal terhubung ke server untuk memproses status pengantaran.',
    };
  }
}


